package com.ijoin.ui.screens

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.FavoriteBorder
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import coil3.compose.AsyncImagePainter
import com.ijoin.data.api.ApiClient
import com.ijoin.data.api.BookingApi
import com.ijoin.data.api.ContactApi
import com.ijoin.data.api.TicketApi
import com.ijoin.data.model.Booking
import com.ijoin.data.model.BookingStatus
import com.ijoin.data.model.Event
import com.ijoin.data.model.EventBookingResult
import com.ijoin.data.model.EventStatus
import com.ijoin.data.model.EventTicketBookingRequest
import com.ijoin.data.model.isFreeEvent
import com.ijoin.data.model.PlatformFee
import com.ijoin.data.model.Ticket
import com.ijoin.data.model.TimeSlot
import com.ijoin.data.model.TimeSlotStatus
import com.ijoin.data.repository.AuthRepository
import com.ijoin.getPlatform
import com.ijoin.analytics.AnalyticsTracker
import com.ijoin.ui.test.TestTags
import com.ijoin.i18n.Strings
import com.ijoin.i18n.strings
import com.ijoin.ui.components.VideoPlayer
import com.ijoin.ui.payment.PaymentFlowArgs
import com.ijoin.ui.util.userFriendlyErrorMessage
import com.ijoin.ui.design.CalendarNavButton
import com.ijoin.ui.design.EventCardAccent
import com.ijoin.ui.design.EventCardAccentDark
import com.ijoin.ui.design.EventCardAccentSoft
import com.ijoin.ui.design.EventCardBackground
import com.ijoin.ui.design.EventCardInputCorner
import com.ijoin.ui.design.EventCardOutline
import com.ijoin.ui.design.EventCardSurface
import com.ijoin.ui.design.EventCardWarm
import com.ijoin.ui.design.EventCardWarmSoft
import com.ijoin.ui.design.EventDialog
import com.ijoin.ui.design.EventOutlineButton
import com.ijoin.ui.design.EventPrimaryButton
import com.ijoin.ui.design.EventSectionCard
import com.ijoin.ui.design.EventStepperButton
import com.ijoin.ui.design.EventTextAction
import com.ijoin.ui.design.eventFilterChipBorder
import com.ijoin.ui.design.eventFilterChipColors
import com.ijoin.ui.design.eventOutlinedFieldColors
import com.ijoin.util.datetime.combineDateAndTime
import com.ijoin.util.datetime.currentDateTimeInTimeZone
import com.ijoin.util.datetime.currentLocalDate
import com.ijoin.util.datetime.monthNumberCompat
import com.ijoin.util.datetime.toLocalDateCompat
import com.ijoin.deeplink.DeepLinkBuilder
import com.ijoin.platform.ShareHandler
import com.ijoin.platform.createTimeZone
import com.ijoin.platform.displayId
import com.ijoin.platform.getLocalTimeZone
import com.ijoin.platform.isWebMobile
import com.ijoin.platform.toInstantInTimeZone
import com.ijoin.platform.toLocalDateTimeInTimeZone
import com.ijoin.ui.components.EventManagementButtons
import com.ijoin.ui.components.NavigationScaffold
import com.ijoin.ui.util.resolveMediaUrl
import com.ijoin.ui.util.toThumbUrl
import kotlinx.datetime.Instant
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.daysUntil
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.plus
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlin.math.abs
import kotlin.math.roundToInt

@Composable
fun EventCardScreen(
    eventId: String,
    isAuthenticated: Boolean,
    onRequireAuth: () -> Unit,
    onBackClick: () -> Unit,
    onBuyTicketClick: (com.ijoin.ui.payment.PaymentFlowArgs) -> Unit,
    onContactOrganizerClick: (String, String?) -> Unit,
    onOpenOrganizerProfile: (String) -> Unit,
    onStartChatWithUser: (String, String?) -> Unit,
    onNavigateToTicket: (String) -> Unit = {},
    onEditEvent: () -> Unit = {},
    onOpenHome: () -> Unit,
    onOpenExplore: () -> Unit,
    onCreateEventClick: () -> Unit,
    onOpenTickets: () -> Unit,
    onOpenProfile: () -> Unit,
    selectedTab: String = "home",
    modifier: Modifier = Modifier,
    viewModel: EventCardViewModel = remember(eventId) { EventCardViewModel(eventId) }
) {
    val strings = strings()
    val uiState by viewModel.uiState.collectAsState()
    val platform = remember { getPlatform() }

    val onTabSelected: (String) -> Unit = { tab ->
        when (tab) {
            "home" -> {
                AnalyticsTracker.trackHomeFromEvent(
                    eventId = uiState.event?.id,
                    contentName = uiState.event?.title
                )
                onOpenHome()
            }
            "explore" -> onOpenExplore()
            "create" -> onCreateEventClick()
            "tickets" -> onOpenTickets()
            "profile" -> onOpenProfile()
        }
    }
    val currentEventId = uiState.event?.id
    val currentEventTitle = uiState.event?.title
    val currentScheduleType = uiState.event?.scheduleType

    LaunchedEffect(currentEventId, currentEventTitle) {
        currentEventId?.let { eventId ->
            AnalyticsTracker.trackViewContent(eventId, currentEventTitle)
        }
    }

    LaunchedEffect(uiState.eventDeleted) {
        if (uiState.eventDeleted) {
            onBackClick()
        }
    }

    NavigationScaffold(
        selectedTab = selectedTab,
        onTabSelected = onTabSelected,
        modifier = modifier.fillMaxSize(),
        containerColor = EventCardBackground,
        topBar = {},
        snackbarHost = {}
    ) { paddingValues ->
        val focusManager = LocalFocusManager.current
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(
                    Brush.verticalGradient(
                        colors = listOf(EventCardBackground, Color.White)
                    )
                )
                .padding(paddingValues)
                .pointerInput(Unit) {
                    detectTapGestures(onTap = { focusManager.clearFocus() })
                }
        ) {
    when {
        uiState.isLoading -> {
                    CircularProgressIndicator(
                        modifier = Modifier.align(Alignment.Center)
                    )
                }
        uiState.error != null -> {
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(16.dp),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Center
                    ) {
                        Text(
                            text = uiState.error ?: "Unknown error",
                            style = MaterialTheme.typography.bodyLarge,
                            color = MaterialTheme.colorScheme.error
                        )
                        Spacer(modifier = Modifier.height(16.dp))
                        Button(onClick = { viewModel.retry() }) {
                            Text(strings.ok)
                        }
                    }
                }
        uiState.event != null -> {
            val slotBookingState = uiState.slotBooking
            val recurringBookingState = uiState.recurringBooking

            LaunchedEffect(slotBookingState.bookingSuccess, slotBookingState.lastBookingId) {
                if (slotBookingState.bookingSuccess && slotBookingState.lastBookingId != null) {
                    onBuyTicketClick(
                        PaymentFlowArgs(
                            eventId = eventId,
                            bookingId = slotBookingState.lastBookingId,
                        )
                    )
                    viewModel.onBookingHandled()
                }
            }

            LaunchedEffect(slotBookingState.issuedTicketId) {
                val ticketId = slotBookingState.issuedTicketId
                if (ticketId != null) {
                    onNavigateToTicket(ticketId)
                    viewModel.onBookingHandled()
                }
            }

            LaunchedEffect(
                recurringBookingState.bookingSuccess,
                recurringBookingState.pendingPaymentBookingId,
            ) {
                val pendingBookingId = recurringBookingState.pendingPaymentBookingId
                if (recurringBookingState.bookingSuccess && pendingBookingId != null) {
                    onBuyTicketClick(
                        PaymentFlowArgs(
                            eventId = eventId,
                            bookingId = pendingBookingId,
                            occurrenceDateTime = recurringBookingState.lastOccurrenceDateTime,
                        )
                    )
                    viewModel.onRecurringBookingHandled()
                }
            }

            LaunchedEffect(recurringBookingState.issuedTicketId) {
                val ticketId = recurringBookingState.issuedTicketId
                if (ticketId != null) {
                    onNavigateToTicket(ticketId)
                    viewModel.onRecurringTicketHandled()
                }
            }

            LaunchedEffect(
                uiState.oneTimeBookingSuccess,
                uiState.oneTimePendingPaymentBookingId,
            ) {
                val pendingBookingId = uiState.oneTimePendingPaymentBookingId
                if (uiState.oneTimeBookingSuccess && pendingBookingId != null) {
                    onBuyTicketClick(
                        PaymentFlowArgs(
                            eventId = eventId,
                            bookingId = pendingBookingId,
                            occurrenceDateTime = uiState.event?.eventDate,
                        )
                    )
                    viewModel.onOneTimeBookingHandled()
                }
            }

                        val constrainedModifier = Modifier
                            .align(Alignment.TopCenter)
                            .fillMaxHeight()
                            .padding(bottom = 8.dp)
                            .widthIn(min = 0.dp, max = 960.dp)

                        EventCardContent(
                            event = uiState.event!!,
                            isFavorite = uiState.isFavorite,
                            slotBookingState = slotBookingState,
                            recurringBookingState = recurringBookingState,
                            oneTimeRemainingTickets = uiState.oneTimeRemainingTickets,
                            oneTimeBookingError = uiState.oneTimeBookingError,
                            oneTimePendingPaymentBookingId = uiState.oneTimePendingPaymentBookingId,
                            onBackClick = onBackClick,
                            onFavoriteClick = { viewModel.toggleFavorite() },
                            onBuyTicketClick = {
                                onBuyTicketClick(
                                    PaymentFlowArgs(
                                        eventId = eventId,
                                        occurrenceDateTime = uiState.event?.eventDate,
                                    )
                                )
                            },
                            onContactOrganizerClick = onContactOrganizerClick,
                            onOpenOrganizerProfile = onOpenOrganizerProfile,
                            onPreviousMonth = viewModel::loadPreviousMonth,
                            onNextMonth = viewModel::loadNextMonth,
                            onDateSelected = viewModel::selectDate,
                            onSlotToggle = viewModel::toggleSlot,
                            onBookSlot = {
                                if (isAuthenticated) {
                                    viewModel.bookSelectedSlots()
                                } else {
                                    onRequireAuth()
                                }
                            },
                            onRecurringPreviousMonth = viewModel::loadRecurringPreviousMonth,
                            onRecurringNextMonth = viewModel::loadRecurringNextMonth,
                            onRecurringDateSelected = viewModel::selectRecurringDate,
                            onRecurringBook = { quantity ->
                                if (isAuthenticated) {
                                    viewModel.bookRecurringOccurrence(quantity)
                                } else {
                                    onRequireAuth()
                                }
                            },
                            onBookOneTime = { quantity ->
                                if (isAuthenticated) {
                                    viewModel.bookOneTime(quantity)
                                } else {
                                    onRequireAuth()
                                }
                            },
                            onStartChatWithUser = onStartChatWithUser,
                            onNavigateToTicket = onNavigateToTicket,
                            isCancellingEvent = uiState.isCancellingEvent,
                            cancelEventError = uiState.cancelEventError,
                            isRestoringEvent = uiState.isRestoringEvent,
                            restoreEventError = uiState.restoreEventError,
                            onCancelEvent = viewModel::cancelEvent,
                            onCancelRecurringDay = viewModel::cancelRecurringDay,
                            onCancelSlotDay = viewModel::cancelSlotDay,
                            onCancelSlots = viewModel::cancelSelectedSlots,
                            onRestoreSlotDay = viewModel::restoreSlotDay,
                            onRestoreSlots = viewModel::restoreSelectedSlots,
                            onRestoreEvent = viewModel::restoreEvent,
                            isDeletingEvent = uiState.isDeletingEvent,
                            deleteEventError = uiState.deleteEventError,
                            onEditEvent = onEditEvent,
                            onDeleteEvent = viewModel::deleteEvent,
                            platformFee = uiState.platformFee,
                            modifier = constrainedModifier,
                        )
                }
            }
        }
    }
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
private fun EventCardContent(
    event: Event,
    isFavorite: Boolean,
    slotBookingState: SlotBookingUiState,
    recurringBookingState: RecurringBookingUiState,
    oneTimeRemainingTickets: Int?,
    oneTimeBookingError: String?,
    oneTimePendingPaymentBookingId: Long?,
    onBackClick: () -> Unit,
    onFavoriteClick: () -> Unit,
    onBuyTicketClick: () -> Unit,
    onContactOrganizerClick: (String, String?) -> Unit,
    onOpenOrganizerProfile: (String) -> Unit,
    onPreviousMonth: () -> Unit,
    onNextMonth: () -> Unit,
    onDateSelected: (LocalDate) -> Unit,
    onSlotToggle: (Long) -> Unit,
    onBookSlot: () -> Unit,
    onRecurringPreviousMonth: () -> Unit,
    onRecurringNextMonth: () -> Unit,
    onRecurringDateSelected: (LocalDate) -> Unit,
    onRecurringBook: (Int) -> Unit,
    onBookOneTime: (Int) -> Unit,
    onStartChatWithUser: (String, String?) -> Unit,
    onNavigateToTicket: (String) -> Unit,
    isCancellingEvent: Boolean,
    cancelEventError: String?,
    isRestoringEvent: Boolean,
    restoreEventError: String?,
    onCancelEvent: (String) -> Unit,
    onCancelRecurringDay: (String) -> Unit,
    onCancelSlotDay: (String) -> Unit,
    onCancelSlots: (String) -> Unit,
    onRestoreSlotDay: () -> Unit,
    onRestoreSlots: () -> Unit,
    onRestoreEvent: () -> Unit,
    isDeletingEvent: Boolean,
    deleteEventError: String?,
    onEditEvent: () -> Unit,
    onDeleteEvent: () -> Unit,
    platformFee: PlatformFee = PlatformFee.DEFAULT,
    modifier: Modifier = Modifier,
) {
    val strings = strings()
    var showDeleteConfirmation by remember { mutableStateOf(false) }
    val scrollState = rememberScrollState()
    val platform = remember { getPlatform() }
    val isMobileView = platform.isMobile || (platform.isWeb && isWebMobile())
    val mobileImages = event.mobileImageUrls?.filterNot { it.isBlank() }.orEmpty()
    val useMobileImages = isMobileView && mobileImages.isNotEmpty()
    val videoUrls = if (useMobileImages) emptySet() else event.videoUrls?.toSet().orEmpty()
    val mediaItems = (if (useMobileImages) mobileImages else event.mediaUrls.orEmpty())
        .mapNotNull { url ->
            val trimmed = url.trim()
            if (trimmed.isEmpty()) {
                null
            } else {
                val isVideo = isVideoUrl(trimmed, videoUrls)
                if (isVideo || isImageUrl(trimmed)) MediaItem(trimmed, isVideo) else null
            }
        }
    val headerAspectRatio = if (isMobileView) 1f else 16f / 9f
    val pagerState = rememberPagerState(pageCount = { maxOf(1, mediaItems.size) })
    val isSlotBooking = slotBookingState.isEnabled
    val isRecurring = recurringBookingState.isEnabled
    val isEventCancelled = event.status == EventStatus.CANCELLED || event.status == EventStatus.DELETED
    val isEventPublished = event.status == EventStatus.PUBLISHED
    val isFreeEvent = event.isFreeEvent()
    val minTierPrice = event.priceTiers?.minOfOrNull { it.price }
    val effectivePrice = minTierPrice ?: event.ticketPrice
    val headerPriceLabel = when {
        isFreeEvent -> strings.free
        effectivePrice != null -> strings.formatPrice(effectivePrice, event.currency)
        else -> ""
    }
    var cancellationDialogTarget by remember { mutableStateOf<CancellationDialogTarget?>(null) }
    var cancellationReason by remember { mutableStateOf("") }
    var cancellationReasonError by remember { mutableStateOf<String?>(null) }
    val authRepo = remember { AuthRepository() }
    val currentUserId = remember { authRepo.getUserId()?.toString() }
    val isOrganizer = currentUserId != null && currentUserId == event.organizerId
    val organizerBookingsState = if (isOrganizer && isSlotBooking) {
        rememberOrganizerBookings(event.id, event.organizerId)
    } else {
        null
    }
    val organizerData = if (isOrganizer) {
        rememberOrganizerData(
            eventId = event.id,
            organizerId = event.organizerId,
        )
    } else {
        null
    }
    val bookingPhonesById = remember(organizerData?.enrichedBookings) {
        organizerData?.enrichedBookings.orEmpty()
            .mapNotNull { enriched ->
                val phone = enriched.contactRequest?.phone?.trim()?.takeIf { it.isNotBlank() }
                phone?.let { enriched.booking.id to it }
            }
            .toMap()
    }
    val organizerBookings = organizerBookingsState?.bookings.orEmpty()
    var selectedBooking by remember { mutableStateOf<Booking?>(null) }
    var oneTimeTicketQuantity by rememberSaveable(event.id) { mutableStateOf(1) }
    var recurringTicketQuantity by rememberSaveable(event.id, recurringBookingState.scheduleType) { mutableStateOf(1) }
    val oneTimeMaxQuantity = oneTimeRemainingTickets?.takeIf { it > 0 }
    val recurringSelectedCapacity = recurringBookingState.selectedDate?.let { date ->
        recurringBookingState.capacityByDate[date]
    }?.takeIf { it > 0 }
    if (oneTimeTicketQuantity < 1) {
        oneTimeTicketQuantity = 1
    }
    if (oneTimeMaxQuantity != null && oneTimeTicketQuantity > oneTimeMaxQuantity) {
        oneTimeTicketQuantity = oneTimeMaxQuantity
    }
    if (recurringTicketQuantity < 1) {
        recurringTicketQuantity = 1
    }
    if (recurringSelectedCapacity != null && recurringTicketQuantity > recurringSelectedCapacity) {
        recurringTicketQuantity = recurringSelectedCapacity
    }

    // Auto-advance slideshow every 4 seconds
    LaunchedEffect(mediaItems.size) {
        if (mediaItems.size > 1) {
            while (true) {
                delay(4000) // 4 seconds
                val nextPage = (pagerState.currentPage + 1) % mediaItems.size
                pagerState.animateScrollToPage(nextPage)
            }
        }
    }

    selectedBooking?.let { booking ->
        val contactRequest = organizerData?.enrichedBookings
            ?.firstOrNull { it.booking.id == booking.id }
            ?.contactRequest
        BookingDetailsDialog(
            booking = booking,
            contactRequest = contactRequest,
            onDismiss = { selectedBooking = null },
            onStartChatWithUser = onStartChatWithUser
        )
    }

    Column(
        modifier = modifier
            .testTag(TestTags.EventCard.ROOT)
            .verticalScroll(scrollState)
    ) {
        val topButtons: @Composable (Modifier) -> Unit = { rowModifier ->
            Row(
                modifier = rowModifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp, vertical = 12.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Surface(
                    color = Color.White.copy(alpha = 0.92f),
                    shape = CircleShape,
                    shadowElevation = 6.dp
                ) {
                    IconButton(
                        onClick = onBackClick,
                        modifier = Modifier
                            .size(44.dp)
                            .testTag(TestTags.EventCard.BACK_BUTTON)
                    ) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = strings.back,
                            tint = EventCardAccentDark
                        )
                    }
                }

                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Surface(
                        color = Color.White.copy(alpha = 0.92f),
                        shape = CircleShape,
                        shadowElevation = 6.dp
                    ) {
                        IconButton(
                            onClick = onFavoriteClick,
                            modifier = Modifier.size(44.dp)
                        ) {
                            Icon(
                                imageVector = if (isFavorite) Icons.Filled.Favorite else Icons.Outlined.FavoriteBorder,
                                contentDescription = if (isFavorite) "Remove from favorites" else "Add to favorites",
                                tint = if (isFavorite) Color(0xFFE53935) else EventCardAccentDark
                            )
                        }
                    }

                    Surface(
                        color = Color.White.copy(alpha = 0.92f),
                        shape = CircleShape,
                        shadowElevation = 6.dp
                    ) {
                        IconButton(
                            onClick = {
                                val shareUrl = DeepLinkBuilder.eventUrl(event.id)
                                ShareHandler.shareUrl(shareUrl, event.title)
                            },
                            modifier = Modifier.size(44.dp)
                        ) {
                            Icon(
                                imageVector = Icons.Default.Share,
                                contentDescription = strings.share,
                                tint = EventCardAccentDark
                            )
                        }
                    }
                }
            }
        }

        if (mediaItems.isNotEmpty()) {
            // Event Image Slideshow with overlay buttons
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .aspectRatio(headerAspectRatio)
            ) {
                // Image slideshow
                HorizontalPager(
                    state = pagerState,
                    modifier = Modifier.fillMaxSize()
                ) { page ->
                    val item = mediaItems[page]
                    val originalUrl = item.url
                    val thumbFallbackWidth = 1080
                    val fullMediaUrl = resolveEventMediaUrl(
                        originalUrl = originalUrl,
                        isVideo = item.isVideo,
                        thumbWidth = null
                    )
                    val thumbMediaUrl = resolveEventMediaUrl(
                        originalUrl = originalUrl,
                        isVideo = item.isVideo,
                        thumbWidth = thumbFallbackWidth
                    )
                    val preferThumb = isMobileView || platform.isWeb
                    val primaryMediaUrl = if (preferThumb) thumbMediaUrl else fullMediaUrl
                    val canFallbackToThumb = !preferThumb && thumbMediaUrl != primaryMediaUrl

                    // Check if this is a video
                    val isVideo = item.isVideo

                    // Log for debugging
                    if (isVideo) {
                        println("EventCardScreen: Loading video - original: $originalUrl, final: $primaryMediaUrl")
                    }

                    if (isVideo) {
                        VideoPlayer(
                            videoUrl = primaryMediaUrl,
                            modifier = Modifier.fillMaxSize()
                        )
                    } else {
                        var useThumbFallback by remember(originalUrl) { mutableStateOf(false) }
                        val imageUrl = if (useThumbFallback) thumbMediaUrl else primaryMediaUrl

                        AsyncImage(
                            model = imageUrl,
                            contentDescription = strings.eventImageContentDescription(event.title ?: "", page + 1),
                            modifier = Modifier.fillMaxSize(),
                            contentScale = ContentScale.Crop,
                            alignment = Alignment.Center,
                            onState = { state ->
                                if (state is AsyncImagePainter.State.Error && canFallbackToThumb && !useThumbFallback) {
                                    useThumbFallback = true
                                }
                            }
                        )
                    }
                }

                // Page indicators (dots)
                if (mediaItems.size > 1) {
                    Row(
                        modifier = Modifier
                            .align(Alignment.BottomCenter)
                            .padding(16.dp),
                        horizontalArrangement = Arrangement.spacedBy(6.dp)
                    ) {
                        repeat(mediaItems.size) { index ->
                            Box(
                                modifier = Modifier
                                    .size(if (pagerState.currentPage == index) 8.dp else 6.dp)
                                    .clip(CircleShape)
                                    .background(
                                        if (pagerState.currentPage == index)
                                            Color.White
                                        else
                                            Color.White.copy(alpha = 0.5f)
                                    )
                            )
                        }
                    }
                }

                // Photo counter (top left)
                if (mediaItems.size > 1) {
                    Surface(
                        color = Color.Black.copy(alpha = 0.6f),
                        shape = MaterialTheme.shapes.small,
                        modifier = Modifier
                            .align(Alignment.BottomEnd)
                            .padding(16.dp)
                    ) {
                        Text(
                            text = "${pagerState.currentPage + 1}/${mediaItems.size}",
                            style = MaterialTheme.typography.labelSmall,
                            color = Color.White,
                            modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp)
                        )
                    }
                }

                // Top buttons overlay
                topButtons(Modifier.align(Alignment.TopStart))
            }
        } else {
            topButtons(Modifier)
        }

        // Main content
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Surface(
                modifier = Modifier.fillMaxWidth(),
                color = EventCardSurface,
                shape = RoundedCornerShape(24.dp),
                shadowElevation = 6.dp,
                border = BorderStroke(1.dp, EventCardOutline)
            ) {
                Column(
                    modifier = Modifier.padding(20.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.Top
                    ) {
                        Column(
                            modifier = Modifier.weight(1f),
                            verticalArrangement = Arrangement.spacedBy(6.dp)
                        ) {
                            Text(
                                text = event.title,
                                style = MaterialTheme.typography.headlineMedium,
                                fontWeight = FontWeight.Bold
                            )
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(6.dp)
                            ) {
                                Icon(
                                    imageVector = Icons.Default.Person,
                                    contentDescription = null,
                                    tint = MaterialTheme.colorScheme.onSurfaceVariant,
                                    modifier = Modifier.size(16.dp)
                                )
                                Text(
                                    text = "${strings.organizerLabel}: ${event.organizerName ?: "Digital Academy"}",
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                        }

                        if (headerPriceLabel.isNotBlank()) {
                            PriceBadge(
                                price = headerPriceLabel,
                                modifier = Modifier.padding(start = 12.dp)
                            )
                        }
                    }

                    Row(
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        if (event.isOnline == true) {
                            EventTag(
                                icon = Icons.Default.Videocam,
                                text = strings.online,
                                backgroundColor = EventCardAccent,
                                textColor = Color.White
                            )
                        }

                        if (event.locationName != null && event.isOnline != true) {
                            EventTag(
                                icon = Icons.Default.LocationOn,
                                text = strings.offline,
                                backgroundColor = EventCardWarmSoft,
                                textColor = EventCardWarm
                            )
                        }
                    }
                }
            }

            event.description?.takeIf { it.isNotBlank() }?.let { description ->
                EventSectionCard(title = strings.description) {
                    Text(
                        text = description,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }

            // Date info card - only for ONE_TIME and WEEKLY events (not for SLOT_BOOKING)
            if (!isSlotBooking) {
                val isOneTimeEvent = !isRecurring
                val userTimeZoneId = remember { getLocalTimeZone().displayId }

                // For recurring events, get the selected date or first available date
                val recurringDisplayDate = if (isRecurring) {
                    recurringBookingState.selectedDate
                        ?: recurringBookingState.capacityByDate.keys.minOrNull()
                        ?: findNextAvailableDate(recurringBookingState.availableDaysOfWeek)
                } else null

                // Get time for recurring events from weeklyTime/monthlyTime
                val recurringTime = if (isRecurring) {
                    when (recurringBookingState.scheduleType) {
                        RecurringScheduleType.WEEKLY -> event.weeklyTime
                        RecurringScheduleType.MONTHLY -> event.monthlyTime
                        null -> event.eventTime
                    }
                } else null

                // Calculate the date content based on event type
                val dateContent = if (isRecurring && recurringDisplayDate != null) {
                    strings.formatSelectedDate(recurringDisplayDate)
                } else {
                    strings.formatEventDate(event.eventDate, event.eventEndDate, event.timeZone)
                }

                // Check if user timezone differs from event timezone
                val showUserTimezone = event.timeZone != null && event.timeZone != userTimeZoneId

                // Calculate time info based on event type
                // When showing two cards, don't include timezone in timeInfo (it's shown separately)
                val timeInfo = if (isRecurring && recurringTime != null) {
                    val timeRange = formatRecurringTimeRange(recurringTime, event.durationMinutes)
                    if (!showUserTimezone) {
                        val tzAbbreviation = event.timeZone?.let { getTimezoneAbbreviation(it) }
                        if (tzAbbreviation != null) "$timeRange ($tzAbbreviation)" else timeRange
                    } else {
                        timeRange
                    }
                } else {
                    formatEventTime(event.eventDate, event.durationMinutes, event.timeZone, includeTimezone = !showUserTimezone)
                }

                // Calculate user timezone info for the second card
                val userTimeInfo = if (showUserTimezone) {
                    if (isRecurring && recurringDisplayDate != null && recurringTime != null) {
                        formatRecurringTimeRangeInUserTimezone(
                            date = recurringDisplayDate,
                            time = recurringTime,
                            durationMinutes = event.durationMinutes,
                            eventTimeZone = event.timeZone!!,
                            userTimeZoneId = userTimeZoneId
                        )
                    } else if (isOneTimeEvent && event.eventDate != null) {
                        formatEventTimeRangeInUserTimezone(
                            instant = event.eventDate,
                            durationMinutes = event.durationMinutes,
                            eventTimeZone = event.timeZone!!,
                            userTimeZoneId = userTimeZoneId
                        )
                    } else null
                } else null

                val userDateContent = if (showUserTimezone) {
                    if (isRecurring && recurringDisplayDate != null && recurringTime != null) {
                        getRecurringDateInUserTimezone(
                            date = recurringDisplayDate,
                            time = recurringTime,
                            eventTimeZone = event.timeZone!!,
                            userTimeZoneId = userTimeZoneId
                        )?.let { strings.formatSelectedDate(it) }
                    } else if (isOneTimeEvent && event.eventDate != null) {
                        getEventDateInUserTimezone(
                            instant = event.eventDate,
                            userTimeZoneId = userTimeZoneId
                        )?.let { strings.formatSelectedDate(it) }
                    } else null
                } else null

                if (showUserTimezone && userTimeInfo != null && userDateContent != null) {
                    // Two cards side by side
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        InfoCardCompact(
                            icon = Icons.Default.CalendarToday,
                            title = dateContent,
                            timeInfo = timeInfo,
                            timezoneLabel = getTimezoneAbbreviation(event.timeZone!!),
                            durationInfo = event.durationMinutes?.let { strings.formatDuration(it) },
                            modifier = Modifier.weight(1f)
                        )
                        InfoCardCompact(
                            icon = Icons.Default.Schedule,
                            title = userDateContent,
                            timeInfo = userTimeInfo,
                            timezoneLabel = getTimezoneAbbreviation(userTimeZoneId),
                            durationInfo = null,
                            modifier = Modifier.weight(1f)
                        )
                    }
                } else {
                    // Single card full width
                    InfoCard(
                        icon = Icons.Default.CalendarToday,
                        title = strings.dateLabel,
                        content = dateContent,
                        timeInfo = timeInfo,
                        modifier = Modifier.fillMaxWidth(),
                        durationInfo = event.durationMinutes?.let { strings.formatDuration(it) },
                        localTimeInfo = null
                    )
                }

                // Show remaining spots for one-time events
                if (isOneTimeEvent && oneTimeRemainingTickets != null) {
                    val total = event.maxCapacity
                    val totalText = total?.let { " ${strings.of} $it" } ?: ""
                    Text(
                        text = "${strings.spotsLeft}: $oneTimeRemainingTickets$totalText",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.testTag(TestTags.EventCard.ONE_TIME_SPOTS_TAG)
                    )
                }
            }

            if (isEventCancelled) {
                EventCancelledBanner(reason = event.cancellationReason)
            }

            // Booking / price section
            if (!isEventCancelled) {
                if (!isEventPublished) {
                    Text(
                        text = strings.unpublishedBookingNotice,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                if (isSlotBooking) {
                    SlotBookingSection(
                        eventId = event.id,
                        event = event,
                        state = slotBookingState,
                        onPreviousMonth = onPreviousMonth,
                        onNextMonth = onNextMonth,
                        onDateSelected = onDateSelected,
                        onSlotToggle = onSlotToggle,
                        onBookClick = { if (isEventPublished) onBookSlot() },
                        isOrganizer = isOrganizer,
                        organizerBookings = organizerBookings,
                        bookingPhonesById = bookingPhonesById,
                        onShowBookingDetails = { selectedBooking = it },
                        onStartChatWithUser = onStartChatWithUser,
                        onRequestCancelSlots = {
                            cancellationReason = ""
                            cancellationReasonError = null
                            cancellationDialogTarget = CancellationDialogTarget.SLOTS
                        },
                        onRequestRestoreSlots = onRestoreSlots,
                        onRequestCancelDay = {
                            cancellationReason = ""
                            cancellationReasonError = null
                            cancellationDialogTarget = CancellationDialogTarget.SLOT_DAY
                        },
                        onRequestRestoreDay = onRestoreSlotDay,
                        requireContactInfo = event.requireContactInfo == true,
                        eventTimeZone = event.timeZone,
                        platformFee = platformFee,
                        bookingEnabled = isEventPublished
                    )
                } else if (isRecurring) {
                    RecurringBookingSection(
                        event = event,
                        state = recurringBookingState,
                        onPreviousMonth = onRecurringPreviousMonth,
                        onNextMonth = onRecurringNextMonth,
                        onDateSelected = onRecurringDateSelected,
                        onBookClick = { qty -> if (isEventPublished) onRecurringBook(qty) },
                        requireContactInfo = event.requireContactInfo == true,
                        ticketQuantity = recurringTicketQuantity,
                        onQuantityChange = { newValue ->
                            val sanitized = newValue.coerceAtLeast(1)
                            val limit = recurringSelectedCapacity
                            recurringTicketQuantity = if (limit != null && limit > 0) {
                                sanitized.coerceAtMost(limit)
                            } else {
                                sanitized
                            }
                        },
                        platformFee = platformFee,
                        bookingEnabled = isEventPublished
                    )
                    if (isOrganizer) {
                        recurringBookingState.dayCancellationError?.let { error ->
                            Text(
                                text = error,
                                color = MaterialTheme.colorScheme.error,
                                style = MaterialTheme.typography.bodyMedium
                            )
                        }
                        Button(
                            onClick = {
                                cancellationReason = ""
                                cancellationReasonError = null
                                cancellationDialogTarget = CancellationDialogTarget.RECURRING_DAY
                            },
                            enabled = recurringBookingState.selectedDate != null && !recurringBookingState.isCancellingDay,
                            modifier = Modifier
                                .fillMaxWidth()
                                .testTag(TestTags.EventCard.RECURRING_CANCEL_DAY_BUTTON),
                            colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error)
                        ) {
                            if (recurringBookingState.isCancellingDay) {
                                CircularProgressIndicator(
                                    modifier = Modifier.size(18.dp),
                                    strokeWidth = 2.dp,
                                    color = MaterialTheme.colorScheme.onError
                                )
                            } else {
                                Icon(
                                    imageVector = Icons.Default.EventBusy,
                                    contentDescription = null,
                                    modifier = Modifier.size(20.dp)
                                )
                                Spacer(modifier = Modifier.width(8.dp))
                                Text(strings.cancelSlotDayAction)
                            }
                        }
                    }
                } else {
                    val minTierPrice = event.priceTiers?.minByOrNull { it.price }?.price
                    val effectivePrice = minTierPrice ?: event.ticketPrice
                    val shouldShowContactForm = event.requireContactInfo == true
                      if (isFreeEvent && shouldShowContactForm) {
                          FreeContactBookingSection(
                              eventId = event.id,
                              onNavigateToTicket = onNavigateToTicket,
                              bookingEnabled = isEventPublished
                          )
                      } else if (isFreeEvent && !shouldShowContactForm) {
                          // Free event without contact form - simple booking button
                          SimpleFreeBookingButton(
                              eventId = event.id,
                              onNavigateToTicket = onNavigateToTicket,
                              bookingEnabled = isEventPublished
                          )
                      } else if (effectivePrice != null) {
                          // Contact form for paid one-time events when requireContactInfo is enabled
                          if (shouldShowContactForm && !isOrganizer) {
                              PaidEventContactSection(
                                  eventId = event.id,
                                quantity = oneTimeTicketQuantity,
                                maxQuantity = oneTimeMaxQuantity,
                                onQuantityChange = { newValue ->
                                    val sanitized = newValue.coerceAtLeast(1)
                                    oneTimeTicketQuantity = if (oneTimeMaxQuantity != null && oneTimeMaxQuantity > 0) {
                                        sanitized.coerceAtMost(oneTimeMaxQuantity)
                                    } else {
                                        sanitized
                                    }
                                },
                                effectivePrice = effectivePrice,
                                currency = event.currency,
                                  onBookClick = { onBookOneTime(oneTimeTicketQuantity) },
                                  bookingError = oneTimeBookingError,
                                  bookingId = oneTimePendingPaymentBookingId,
                                  platformFee = platformFee,
                                  bookingEnabled = isEventPublished
                              )
                          } else {
                            TicketQuantitySelector(
                                quantity = oneTimeTicketQuantity,
                                maxQuantity = oneTimeMaxQuantity,
                                available = oneTimeMaxQuantity,
                                onQuantityChange = { newValue ->
                                    val sanitized = newValue.coerceAtLeast(1)
                                    oneTimeTicketQuantity = if (oneTimeMaxQuantity != null && oneTimeMaxQuantity > 0) {
                                        sanitized.coerceAtMost(oneTimeMaxQuantity)
                                    } else {
                                        sanitized
                                    }
                                },
                                decreaseButtonTag = TestTags.EventCard.ONE_TIME_QUANTITY_DECREASE,
                                increaseButtonTag = TestTags.EventCard.ONE_TIME_QUANTITY_INCREASE,
                                valueTag = TestTags.EventCard.ONE_TIME_QUANTITY_VALUE
                            )
                            Spacer(modifier = Modifier.height(8.dp))
                            val ticketTotal = effectivePrice * oneTimeTicketQuantity
                            val serviceFee = platformFee.calculateFee(ticketTotal)
                            val grandTotal = ticketTotal + serviceFee

                            // Price breakdown
                            if (platformFee.enabled && serviceFee > 0) {
                                Column(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(horizontal = 4.dp, vertical = 8.dp)
                                ) {
                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.SpaceBetween
                                    ) {
                                        Text(
                                            text = if (oneTimeTicketQuantity > 1) {
                                                "${strings.formatPrice(effectivePrice, event.currency)} x $oneTimeTicketQuantity"
                                            } else {
                                                strings.costLabel
                                            },
                                            style = MaterialTheme.typography.bodyMedium,
                                            color = MaterialTheme.colorScheme.onSurfaceVariant
                                        )
                                        Text(
                                            text = strings.formatPrice(ticketTotal, event.currency),
                                            style = MaterialTheme.typography.bodyMedium
                                        )
                                    }
                                    Spacer(modifier = Modifier.height(4.dp))
                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.SpaceBetween
                                    ) {
                                        Text(
                                            text = strings.serviceFeePercent((platformFee.feePercent * 100).toInt()),
                                            style = MaterialTheme.typography.bodyMedium,
                                            color = MaterialTheme.colorScheme.onSurfaceVariant
                                        )
                                        Text(
                                            text = strings.formatPrice(serviceFee, event.currency),
                                            style = MaterialTheme.typography.bodyMedium
                                        )
                                    }
                                    Spacer(modifier = Modifier.height(4.dp))
                                    HorizontalDivider()
                                    Spacer(modifier = Modifier.height(4.dp))
                                    Row(
                                        modifier = Modifier.fillMaxWidth(),
                                        horizontalArrangement = Arrangement.SpaceBetween
                                    ) {
                                        Text(
                                            text = strings.totalAmount,
                                            style = MaterialTheme.typography.bodyMedium,
                                            fontWeight = FontWeight.Bold
                                        )
                                        Text(
                                            text = strings.formatPrice(grandTotal, event.currency),
                                            style = MaterialTheme.typography.bodyMedium,
                                            fontWeight = FontWeight.Bold
                                        )
                                    }
                                }
                                Spacer(modifier = Modifier.height(8.dp))
                            }

                              Button(
                                  onClick = { onBookOneTime(oneTimeTicketQuantity) },
                                  modifier = Modifier
                                      .fillMaxWidth()
                                      .height(64.dp)
                                      .testTag(TestTags.EventCard.ONE_TIME_BOOK_BUTTON),
                                  colors = ButtonDefaults.buttonColors(
                                      containerColor = EventCardAccent,
                                      contentColor = Color.White
                                  ),
                                  shape = MaterialTheme.shapes.medium,
                                  enabled = isEventPublished
                              ) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                            Text(
                                text = "${strings.costLabel} ${strings.formatPrice(grandTotal, event.currency)}",
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Bold
                                )
                                if (oneTimeTicketQuantity > 1) {
                                    Text(
                                        text = "x$oneTimeTicketQuantity",
                                        style = MaterialTheme.typography.bodyLarge,
                                        fontWeight = FontWeight.SemiBold
                                    )
                                }
                                Icon(
                                    imageVector = Icons.Default.ConfirmationNumber,
                                    contentDescription = null,
                                    modifier = Modifier.size(32.dp)
                                )
                            }
                        }

                        oneTimeBookingError?.let { error ->
                            Spacer(modifier = Modifier.height(8.dp))
                            Text(
                                text = error,
                                color = MaterialTheme.colorScheme.error,
                                style = MaterialTheme.typography.bodyMedium
                            )
                        }
                        }
                    }
                }
            }

            // Hide dress code, location, description, and reviews when landing page is enabled
            //if (event.enableLandingPage != true) {
            EventSectionCard(title = strings.eventDetailsTitle) {
                InfoRow(
                    icon = Icons.Default.Checkroom,
                    title = strings.dressCode,
                    content = "Casual / Business Casual"
                )

                event.locationName?.let { location ->
                    InfoRow(
                        icon = Icons.Default.LocationOn,
                        title = strings.location,
                        content = location
                    )
                }
            }

            // Hidden Info section (only shown if user has a ticket)
            event.hiddenInfo?.let { hiddenInfo ->
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    colors = CardDefaults.cardColors(
                        containerColor = Color(0xFFFFF3E0) // Light orange background
                    ),
                    shape = RoundedCornerShape(12.dp)
                ) {
                    Column(
                        modifier = Modifier.padding(16.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            Icon(
                                Icons.Default.Lock,
                                contentDescription = null,
                                tint = Color(0xFFE65100),
                                modifier = Modifier.size(20.dp)
                            )
                            Text(
                                text = strings.hiddenInfo,
                                style = MaterialTheme.typography.titleSmall,
                                fontWeight = FontWeight.Bold,
                                color = Color(0xFFE65100)
                            )
                        }
                        SelectionContainer {
                            Text(
                                text = hiddenInfo,
                                style = MaterialTheme.typography.bodyMedium,
                                color = Color(0xFF5D4037)
                            )
                        }
                    }
                }
            }

            EventSectionCard {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = strings.reviews,
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold
                    )
                    EventTextAction(
                        text = strings.allReviews,
                        onClick = { /* TODO: Show all reviews */ }
                    )
                }

                // Sample review (mocked data)
                ReviewCard(
                    reviewerName = strings.sampleReviewerName,
                    rating = 5.0,
                    timeAgo = strings.sampleReviewTimeAgo,
                    avatarUrl = null
                )
            }

            Spacer(modifier = Modifier.height(8.dp))
            //}

            // Contact organizer button
            val organizerId = event.organizerId
            val organizerName = event.organizerName
            val canContact = !organizerId.isNullOrBlank()
            Button(
                onClick = {
                    organizerId?.let { onContactOrganizerClick(it, organizerName) }
                },
                modifier = Modifier
                    .fillMaxWidth()
                    .height(56.dp)
                    .testTag(TestTags.EventCard.CONTACT_ORGANIZER_BUTTON),
                colors = ButtonDefaults.buttonColors(
                    containerColor = EventCardAccent,
                    contentColor = Color.White
                ),
                shape = MaterialTheme.shapes.medium,
                enabled = canContact
            ) {
                Icon(
                    imageVector = Icons.Default.Chat,
                    contentDescription = null,
                    modifier = Modifier.size(24.dp)
                )
                Spacer(modifier = Modifier.width(8.dp))
                Text(
                    text = strings.contactOrganizer,
                    style = MaterialTheme.typography.titleMedium
                )
            }

            if (canContact) {
                EventOutlineButton(
                    text = strings.userCardTitle,
                    onClick = { organizerId?.let(onOpenOrganizerProfile) },
                    modifier = Modifier.fillMaxWidth()
                )
            }

            Spacer(modifier = Modifier.height(16.dp))

            // Organizer sections: Bookings + Unconfirmed requests
            if (isOrganizer) {
                OrganizerActionsCard(
                    isEventCancelled = isEventCancelled,
                    isCancellingEvent = isCancellingEvent,
                    cancelEventError = cancelEventError,
                    isRestoringEvent = isRestoringEvent,
                    restoreEventError = restoreEventError,
                    isDeletingEvent = isDeletingEvent,
                    deleteEventError = deleteEventError,
                    onRequestCancelEvent = {
                        cancellationReason = ""
                        cancellationReasonError = null
                        cancellationDialogTarget = CancellationDialogTarget.EVENT
                    },
                    onRequestRestoreEvent = onRestoreEvent,
                    onEditEvent = onEditEvent,
                    onRequestDeleteEvent = { showDeleteConfirmation = true },
                )

                // Section 1: Bookings with enriched data
                BookingsSection(
                    organizerData = organizerData,
                    eventId = event.id,
                    isSlotBooking = isSlotBooking,
                    isRecurring = isRecurring,
                    selectedDate = if (isSlotBooking) slotBookingState.selectedDate
                                   else recurringBookingState.selectedDate,
                    onStartChatWithUser = onStartChatWithUser,
                )

                Spacer(modifier = Modifier.height(16.dp))

                // Section 2: Unconfirmed contact requests (without bookingId)
                UnconfirmedRequestsSection(
                    requests = organizerData?.unconfirmedRequests.orEmpty(),
                    isLoading = organizerData?.isLoading ?: false,
                    error = organizerData?.error,
                    onStartChatWithUser = onStartChatWithUser,
                    onRefresh = { organizerData?.refresh?.invoke() },
                )
            }
        }
    }

    cancellationDialogTarget?.let { target ->
        CancellationReasonDialog(
            target = target,
            selectedDate = slotBookingState.selectedDate,
            selectedSlotCount = slotBookingState.selectedSlotIds.size,
            reason = cancellationReason,
            error = cancellationReasonError,
            onReasonChange = {
                cancellationReason = it
                cancellationReasonError = null
            },
            onDismiss = { cancellationDialogTarget = null },
            onConfirm = {
                val trimmed = cancellationReason.trim()
                if (trimmed.isBlank()) {
                    cancellationReasonError = strings.cancellationReasonRequired
                } else {
                    when (target) {
                        CancellationDialogTarget.EVENT -> onCancelEvent(trimmed)
                        CancellationDialogTarget.RECURRING_DAY -> onCancelRecurringDay(trimmed)
                        CancellationDialogTarget.SLOT_DAY -> onCancelSlotDay(trimmed)
                        CancellationDialogTarget.SLOTS -> onCancelSlots(trimmed)
                    }
                    cancellationDialogTarget = null
                }
            }
        )
    }

    if (showDeleteConfirmation) {
        AlertDialog(
            onDismissRequest = { showDeleteConfirmation = false },
            title = { Text(strings.deleteEventTitle) },
            text = { Text(strings.deleteEventMessage) },
            confirmButton = {
                EventTextAction(
                    text = strings.delete,
                    onClick = {
                        onDeleteEvent()
                        showDeleteConfirmation = false
                    }
                )
            },
            dismissButton = {
                EventTextAction(
                    text = strings.cancel,
                    onClick = { showDeleteConfirmation = false }
                )
            }
        )
    }
}

@Composable
private fun OrganizerPanel(eventId: String, onStartChatWithUser: (String, String?) -> Unit) {
    val strings = strings()
    val api = remember { ContactApi() }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var items by remember { mutableStateOf<List<ContactApi.ContactRequestDto>>(emptyList()) }
    var page by remember { mutableStateOf(0) }
    var size by remember { mutableStateOf(10) }
    var totalPages by remember { mutableStateOf(1) }
    var showAll by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(eventId, page, size, showAll) {
        isLoading = true
        error = null
        val reqPage = if (showAll) 0 else page
        val reqSize = if (showAll) 1000 else size
        api.listPaged(eventId, reqPage, reqSize)
            .onSuccess { pr ->
                items = pr.content.filterNotNull()
                totalPages = if (showAll) 1 else pr.totalPages
                if (!showAll) page = pr.number // sync if backend clamps
            }
            .onFailure { error = it.message }
        isLoading = false
    }

    Spacer(modifier = Modifier.height(8.dp))
    Text(
        text = strings.contactInfo,
        style = MaterialTheme.typography.titleMedium,
        fontWeight = FontWeight.Bold
    )
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
        shape = RoundedCornerShape(12.dp)
    ) {
        Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            if (isLoading) {
                CircularProgressIndicator()
            } else if (error != null) {
                Text(text = error ?: "", color = MaterialTheme.colorScheme.error)
            } else if (items.isEmpty()) {
                Text(text = strings.noResults, color = MaterialTheme.colorScheme.onSurfaceVariant)
            } else {
                items.forEach { r ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Column {
                            r.phone?.let { Text("${strings.phone}: $it") }
                            r.comment?.let { Text("${strings.comment}: $it") }
                        }
                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            r.createdAt?.let { Text(formatDateTime(it), style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant) }
                            r.userId?.toString()?.let { uid ->
                                IconButton(onClick = { onStartChatWithUser(uid, r.phone) }) {
                                    Icon(Icons.Default.Send, contentDescription = strings.send)
                                }
                            }
                        }
                    }
                    Divider()
                }
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    EventTextAction(
                        text = strings.prev,
                        onClick = { if (page > 0) page -= 1 },
                        enabled = page > 0
                    )
                    Text("${page + 1} / $totalPages")
                    EventTextAction(
                        text = strings.next,
                        onClick = { if (page + 1 < totalPages) page += 1 },
                        enabled = page + 1 < totalPages
                    )
                    Spacer(Modifier.weight(1f))
                    EventTextAction(
                        text = if (showAll) strings.collapse else strings.showAll,
                        onClick = { showAll = !showAll }
                    )
                    EventTextAction(
                        text = strings.refresh,
                        leadingIcon = Icons.Default.Refresh,
                        onClick = {
                            scope.launch {
                                isLoading = true
                                api.listPaged(eventId, page, size)
                                    .onSuccess { pr ->
                                        items = pr.content.filterNotNull()
                                        totalPages = pr.totalPages
                                    }
                                    .onFailure { error = it.message }
                                isLoading = false
                            }
                        }
                    )
                }
            }
        }
    }
}

private enum class TicketsFilterMode {
    ALL, TODAY, THIS_MONTH, SELECTED_DATE
}

@Composable
private fun TicketsPanel(
    eventId: String,
    isRecurring: Boolean,
    selectedDate: LocalDate?,
    eventTimeZone: String?,
    onStartChatWithUser: (String, String?) -> Unit
) {
    val strings = strings()
    val api = remember { TicketApi() }
    val today = remember { currentLocalDate() }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var items by remember { mutableStateOf<List<com.ijoin.data.model.Ticket>>(emptyList()) }
    var page by remember { mutableStateOf(0) }
    var size by remember { mutableStateOf(10) }
    var totalPages by remember { mutableStateOf(1) }
    var totalElements by remember { mutableStateOf(0) }
    var filterMode by remember { mutableStateOf(if (isRecurring) TicketsFilterMode.TODAY else TicketsFilterMode.ALL) }
    val chipColors = eventFilterChipColors()

    LaunchedEffect(eventId, page, size) {
        isLoading = true
        error = null
        api.getEventTickets(eventId, page, size)
            .onSuccess { pr ->
                items = pr.content.filterNotNull()
                totalPages = pr.totalPages
                totalElements = pr.totalElements.toInt()
                page = pr.number
            }
            .onFailure { error = it.message }
        isLoading = false
    }

    val filteredItems = if (!isRecurring) {
        items
    } else {
        when (filterMode) {
            TicketsFilterMode.ALL -> items
            TicketsFilterMode.TODAY -> items.filter { ticket ->
                ticket.occurrenceDateTime?.let { occ ->
                    val ticketDate = occ.toLocalDateCompat(eventTimeZone)
                    ticketDate == today
                } ?: false
            }
            TicketsFilterMode.THIS_MONTH -> items.filter { ticket ->
                ticket.occurrenceDateTime?.let { occ ->
                    val ticketDate = occ.toLocalDateCompat(eventTimeZone)
                    ticketDate.year == today.year && ticketDate.monthNumberCompat() == today.monthNumberCompat()
                } ?: false
            }
            TicketsFilterMode.SELECTED_DATE -> {
                if (selectedDate != null) {
                    items.filter { ticket ->
                        ticket.occurrenceDateTime?.let { occ ->
                            val ticketDate = occ.toLocalDateCompat(eventTimeZone)
                            ticketDate == selectedDate
                        } ?: false
                    }
                } else {
                    items
                }
            }
        }
    }

    Spacer(modifier = Modifier.height(8.dp))
    Text(
        text = strings.tickets,
        style = MaterialTheme.typography.titleMedium,
        fontWeight = FontWeight.Bold
    )
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
        shape = RoundedCornerShape(12.dp)
    ) {
        Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            // Toggle switch for filtering (only for recurring events)
            if (isRecurring) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    FilterChip(
                        selected = filterMode == TicketsFilterMode.TODAY,
                        onClick = { filterMode = TicketsFilterMode.TODAY },
                        label = { Text(strings.showToday) },
                        colors = chipColors,
                        border = eventFilterChipBorder(selected = filterMode == TicketsFilterMode.TODAY)
                    )
                    FilterChip(
                        selected = filterMode == TicketsFilterMode.THIS_MONTH,
                        onClick = { filterMode = TicketsFilterMode.THIS_MONTH },
                        label = { Text(strings.showThisMonth) },
                        colors = chipColors,
                        border = eventFilterChipBorder(selected = filterMode == TicketsFilterMode.THIS_MONTH)
                    )
                    FilterChip(
                        selected = filterMode == TicketsFilterMode.ALL,
                        onClick = { filterMode = TicketsFilterMode.ALL },
                        label = { Text(strings.showAllBookings) },
                        colors = chipColors,
                        border = eventFilterChipBorder(selected = filterMode == TicketsFilterMode.ALL)
                    )
                    FilterChip(
                        selected = filterMode == TicketsFilterMode.SELECTED_DATE,
                        onClick = { filterMode = TicketsFilterMode.SELECTED_DATE },
                        label = {
                            Text(
                                if (selectedDate != null) strings.formatSelectedDate(selectedDate)
                                else strings.showSelectedDate
                            )
                        },
                        enabled = selectedDate != null,
                        colors = chipColors,
                        border = eventFilterChipBorder(
                            selected = filterMode == TicketsFilterMode.SELECTED_DATE,
                            enabled = selectedDate != null
                        )
                    )
                }
            }
            if (isLoading) {
                CircularProgressIndicator()
            } else if (error != null) {
                Text(text = error ?: "", color = MaterialTheme.colorScheme.error)
            } else if (filteredItems.isEmpty()) {
                Text(
                    text = when {
                        !isRecurring -> strings.noResults
                        filterMode == TicketsFilterMode.SELECTED_DATE -> strings.noTicketsForSelectedDate
                        filterMode == TicketsFilterMode.TODAY -> strings.noTicketsForSelectedDate
                        else -> strings.noResults
                    },
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            } else {
                // Total count
                val displayCount = if (filterMode == TicketsFilterMode.ALL && !isRecurring) {
                    totalElements
                } else {
                    filteredItems.size
                }
                Text(
                    text = strings.totalCount(displayCount),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                filteredItems.forEach { t ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        val name = t.holderName ?: ""
                        val email = t.holderEmail ?: ""
                        val holderText = buildString {
                            if (name.isNotBlank()) append(name)
                            if (email.isNotBlank()) {
                                if (isNotEmpty()) append(" • ")
                                append(email)
                            }
                            if (isBlank()) append(t.userId)
                        }
                        val ticketDate = t.occurrenceDateTime?.toLocalDateCompat(eventTimeZone)
                        val priceText = t.paidPrice?.let { strings.formatPrice(it, null) } ?: ""
                        Column(modifier = Modifier.weight(1f)) {
                            Text(holderText)
                            if (ticketDate != null) {
                                Text(
                                    text = strings.formatSelectedDate(ticketDate),
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                        }
                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            if (priceText.isNotBlank()) {
                                Text(priceText, style = MaterialTheme.typography.bodySmall)
                            }
                            Text(t.ticketNumber, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                            IconButton(onClick = { onStartChatWithUser(t.userId, if (name.isNotBlank()) name else email) }) {
                                Icon(Icons.Default.Send, contentDescription = strings.send)
                            }
                        }
                    }
                    Divider()
                }
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    EventTextAction(
                        text = strings.prev,
                        onClick = { if (page > 0) page -= 1 },
                        enabled = page > 0
                    )
                    Text("${page + 1} / $totalPages")
                    EventTextAction(
                        text = strings.next,
                        onClick = { if (page + 1 < totalPages) page += 1 },
                        enabled = page + 1 < totalPages
                    )
                }
            }
        }
    }
}

private data class OrganizerBookingsState(
    val isLoading: Boolean,
    val error: String?,
    val bookings: List<Booking>,
    val refresh: () -> Unit,
)

private data class EnrichedBooking(
    val booking: Booking,
    val tickets: List<Ticket>,
    val contactRequest: ContactApi.ContactRequestDto?,
    val payment: BookingApi.PaymentDto? = null,
)

private data class OrganizerDataState(
    val isLoading: Boolean = true,
    val error: String? = null,
    val enrichedBookings: List<EnrichedBooking> = emptyList(),
    val unconfirmedRequests: List<ContactApi.ContactRequestDto> = emptyList(),
    val refresh: () -> Unit = {},
)

@Composable
private fun rememberOrganizerBookings(
    eventId: String,
    organizerId: String?,
): OrganizerBookingsState? {
    if (organizerId.isNullOrBlank()) return null
    val api = remember { BookingApi() }
    val scope = rememberCoroutineScope()
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var bookings by remember { mutableStateOf<List<Booking>>(emptyList()) }

    val refresh: () -> Unit = remember(eventId, organizerId) {
        {
            scope.launch {
                isLoading = true
                error = null
                api.getServiceProviderBookings(organizerId)
                    .onSuccess { result ->
                        bookings = result.filter { it.eventId == eventId && it.timeSlots.isNotEmpty() }
                    }
                    .onFailure {
                        error = it.message
                        bookings = emptyList()
                    }
                isLoading = false
            }
            Unit
        }
    }

    LaunchedEffect(refresh) {
        refresh()
    }

    return OrganizerBookingsState(
        isLoading = isLoading,
        error = error,
        bookings = bookings,
        refresh = refresh,
    )
}

@Composable
private fun rememberOrganizerData(
    eventId: String,
    organizerId: String?,
): OrganizerDataState? {
    if (organizerId.isNullOrBlank()) return null

    val bookingApi = remember { BookingApi() }
    val scope = rememberCoroutineScope()

    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var enrichedBookings by remember { mutableStateOf<List<EnrichedBooking>>(emptyList()) }
    var unconfirmedRequests by remember { mutableStateOf<List<ContactApi.ContactRequestDto>>(emptyList()) }

    val refresh: () -> Unit = remember(eventId, organizerId) {
        {
            scope.launch {
                isLoading = true
                error = null

                try {
                    // Use the new enriched bookings API
                    val result = bookingApi.getEnrichedBookings(eventId)
                    result.onSuccess { response ->
                        // Convert BookingApi DTOs to local models
                        enrichedBookings = response.enrichedBookings.map { dto ->
                            EnrichedBooking(
                                booking = dto.booking,
                                tickets = dto.tickets,
                                contactRequest = dto.contactRequest?.let { cr ->
                                    ContactApi.ContactRequestDto(
                                        id = cr.id,
                                        eventId = cr.eventId,
                                        userId = cr.userId,
                                        phone = cr.phone,
                                        comment = cr.comment,
                                        bookingId = cr.bookingId,
                                        formData = cr.formData,
                                        createdAt = cr.createdAt,
                                    )
                                },
                                payment = dto.payment,
                            )
                        }
                        unconfirmedRequests = response.unconfirmedRequests.map { cr ->
                            ContactApi.ContactRequestDto(
                                id = cr.id,
                                eventId = cr.eventId,
                                userId = cr.userId,
                                phone = cr.phone,
                                comment = cr.comment,
                                bookingId = cr.bookingId,
                                formData = cr.formData,
                                createdAt = cr.createdAt,
                            )
                        }
                        error = null
                    }.onFailure { e ->
                        error = e.message
                        enrichedBookings = emptyList()
                        unconfirmedRequests = emptyList()
                    }
                } catch (e: Exception) {
                    error = e.message
                    enrichedBookings = emptyList()
                    unconfirmedRequests = emptyList()
                }

                isLoading = false
            }
            Unit
        }
    }

    LaunchedEffect(refresh) {
        refresh()
    }

    return OrganizerDataState(
        isLoading = isLoading,
        error = error,
        enrichedBookings = enrichedBookings,
        unconfirmedRequests = unconfirmedRequests,
        refresh = refresh,
    )
}

private enum class BookingsFilterMode {
    ALL, TODAY, THIS_MONTH, SELECTED_DATE
}

@Composable
private fun SlotBookingsPanel(
    bookingsState: OrganizerBookingsState,
    selectedDate: LocalDate?,
    onStartChatWithUser: (String, String?) -> Unit,
) {
    val strings = strings()
    val today = remember { currentLocalDate() }
    var expandedBookingId by remember { mutableStateOf<Long?>(null) }
    var filterMode by remember { mutableStateOf(BookingsFilterMode.TODAY) }
    val chipColors = eventFilterChipColors()

    val allBookings = bookingsState.bookings.sortedWith(compareBy { booking ->
        booking.timeSlots.minByOrNull { slot -> slot.startTime }?.startTime
    })

    val todayBookings = allBookings.filter { booking ->
        booking.timeSlots.any { slot ->
            slot.startTime.toLocalDateCompat() == today
        }
    }

    val monthBookings = allBookings.filter { booking ->
        val firstSlotDate = booking.timeSlots.minByOrNull { it.startTime }?.startTime?.toLocalDateCompat()
        firstSlotDate?.let { date ->
            date.year == today.year && date.monthNumberCompat() == today.monthNumberCompat()
        } ?: false
    }

    val filteredBookings = when (filterMode) {
        BookingsFilterMode.ALL -> allBookings
        BookingsFilterMode.TODAY -> todayBookings
        BookingsFilterMode.THIS_MONTH -> monthBookings
        BookingsFilterMode.SELECTED_DATE -> {
            if (selectedDate != null) {
                allBookings.filter { booking ->
                    booking.timeSlots.any { slot ->
                        slot.startTime.toLocalDateCompat() == selectedDate
                    }
                }
            } else {
                todayBookings
            }
        }
    }

    Spacer(modifier = Modifier.height(8.dp))
    Text(
        text = strings.slotBookingsTitle,
        style = MaterialTheme.typography.titleMedium,
        fontWeight = FontWeight.Bold
    )
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant),
        shape = RoundedCornerShape(12.dp)
    ) {
        Column(modifier = Modifier.padding(12.dp)) {
            // Toggle switch for filtering
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                FilterChip(
                    selected = filterMode == BookingsFilterMode.TODAY,
                    onClick = { filterMode = BookingsFilterMode.TODAY },
                    label = { Text(strings.showToday) },
                    colors = chipColors,
                    border = eventFilterChipBorder(selected = filterMode == BookingsFilterMode.TODAY)
                )
                FilterChip(
                    selected = filterMode == BookingsFilterMode.THIS_MONTH,
                    onClick = { filterMode = BookingsFilterMode.THIS_MONTH },
                    label = { Text(strings.showThisMonth) },
                    colors = chipColors,
                    border = eventFilterChipBorder(selected = filterMode == BookingsFilterMode.THIS_MONTH)
                )
                FilterChip(
                    selected = filterMode == BookingsFilterMode.ALL,
                    onClick = { filterMode = BookingsFilterMode.ALL },
                    label = { Text(strings.showAllBookings) },
                    colors = chipColors,
                    border = eventFilterChipBorder(selected = filterMode == BookingsFilterMode.ALL)
                )
                FilterChip(
                    selected = filterMode == BookingsFilterMode.SELECTED_DATE,
                    onClick = { filterMode = BookingsFilterMode.SELECTED_DATE },
                    label = {
                        Text(
                            if (selectedDate != null) strings.formatSelectedDate(selectedDate)
                            else strings.showSelectedDate
                        )
                    },
                    enabled = selectedDate != null,
                    colors = chipColors,
                    border = eventFilterChipBorder(
                        selected = filterMode == BookingsFilterMode.SELECTED_DATE,
                        enabled = selectedDate != null
                    )
                )
            }
            Spacer(modifier = Modifier.height(8.dp))
            when {
                bookingsState.isLoading -> {
                    CircularProgressIndicator()
                }
                bookingsState.error != null -> {
                    Text(text = bookingsState.error ?: "", color = MaterialTheme.colorScheme.error)
                    EventTextAction(
                        text = strings.refresh,
                        leadingIcon = Icons.Default.Refresh,
                        onClick = bookingsState.refresh
                    )
                }
                filteredBookings.isEmpty() -> {
                    Text(
                        text = when (filterMode) {
                            BookingsFilterMode.SELECTED_DATE -> strings.noBookingsForSelectedDate
                            BookingsFilterMode.TODAY -> strings.noBookingsForSelectedDate
                            else -> strings.slotBookingsEmptyMessage
                        },
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                else -> {
                    // Total count
                    Text(
                        text = strings.totalCount(filteredBookings.size),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.padding(bottom = 8.dp)
                    )
                    filteredBookings.forEachIndexed { index, booking ->
                        SlotBookingListItem(
                            booking = booking,
                            isExpanded = expandedBookingId == booking.id,
                            onToggleExpanded = {
                                expandedBookingId = if (expandedBookingId == booking.id) null else booking.id
                            },
                            onStartChatWithUser = onStartChatWithUser
                        )
                        if (index < filteredBookings.lastIndex) {
                            Divider(modifier = Modifier.padding(vertical = 12.dp))
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun SlotBookingListItem(
    booking: Booking,
    isExpanded: Boolean,
    onToggleExpanded: () -> Unit,
    onStartChatWithUser: (String, String?) -> Unit,
) {
    val strings = strings()
    val sortedSlots = remember(booking.timeSlots) {
        booking.timeSlots.sortedBy { it.startTime }
    }
    val firstSlot = sortedSlots.firstOrNull()
    val lastSlot = sortedSlots.lastOrNull()
    val attendeeName = remember(booking) { formatClientDisplayName(booking) }
    val attendeeContact = booking.clientEmail?.takeIf { it.isNotBlank() }
    val dateLabel = firstSlot?.startTime?.toLocalDateCompat()?.let { strings.formatSelectedDate(it) }
    val timeRange = when {
        firstSlot != null && lastSlot != null -> formatSlotRange(firstSlot.startTime, lastSlot.endTime)
        firstSlot != null -> formatSlotRange(firstSlot)
        else -> null
    }
    val chatNameHint = attendeeName
    val colorScheme = MaterialTheme.colorScheme
    val statusColor = when (booking.status) {
        BookingStatus.CONFIRMED -> colorScheme.primary
        BookingStatus.COMPLETED -> colorScheme.secondary
        BookingStatus.CANCELLED,
        BookingStatus.NO_SHOW -> colorScheme.error
        BookingStatus.PENDING,
        BookingStatus.RESERVED -> colorScheme.tertiary
        BookingStatus.EXPIRED -> colorScheme.error.copy(alpha = 0.7f)
    }

    Column(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Column(modifier = Modifier.weight(1f)) {
                dateLabel?.let {
                    Text(
                        text = it,
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.SemiBold
                    )
                }
                timeRange?.let {
                    Text(
                        text = it,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                Text(
                    text = attendeeName,
                    style = MaterialTheme.typography.bodyMedium
                )
                attendeeContact?.let {
                    EmailLinkText(
                        text = it,
                        email = it,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
            Surface(
                shape = RoundedCornerShape(16.dp),
                color = statusColor.copy(alpha = 0.12f),
                border = BorderStroke(1.dp, statusColor)
            ) {
                Text(
                    text = strings.bookingStatusText(booking.status),
                    modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
                    style = MaterialTheme.typography.labelMedium,
                    color = statusColor
                )
            }
            IconButton(onClick = {
                onStartChatWithUser(booking.clientId.toString(), chatNameHint)
            }) {
                Icon(Icons.Default.Send, contentDescription = strings.send)
            }
        }

        booking.clientNotes?.takeIf { it.isNotBlank() }?.let { notes ->
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = strings.clientNotesLabel,
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            Text(
                text = notes,
                style = MaterialTheme.typography.bodyMedium
            )
        }

        if (isExpanded) {
            Spacer(modifier = Modifier.height(8.dp))
            sortedSlots.forEach { slot ->
                val slotDate = strings.formatSelectedDate(slot.startTime.toLocalDateCompat())
                Text(
                    text = "$slotDate • ${formatSlotRange(slot)}",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            Spacer(modifier = Modifier.height(8.dp))
            EventTextAction(
                text = strings.openChat,
                leadingIcon = Icons.Default.Send,
                onClick = { onStartChatWithUser(booking.clientId.toString(), chatNameHint) }
            )
        }

        EventTextAction(
            text = if (isExpanded) strings.hideDetails else strings.viewDetails,
            onClick = onToggleExpanded,
            trailingIcon = if (isExpanded) Icons.Default.KeyboardArrowUp else Icons.Default.KeyboardArrowDown,
            modifier = Modifier.align(Alignment.End)
        )
    }
}

@Composable
private fun BookingDetailsDialog(
    booking: Booking,
    contactRequest: ContactApi.ContactRequestDto?,
    onDismiss: () -> Unit,
    onStartChatWithUser: (String, String?) -> Unit,
) {
    val strings = strings()
    val sortedSlots = remember(booking.timeSlots) {
        booking.timeSlots.sortedBy { it.startTime }
    }
    val attendeeName = formatClientDisplayName(booking)
    val attendeeFirstName = booking.clientFirstName?.trim()?.takeIf { it.isNotBlank() }
    val attendeeEmail = booking.clientEmail?.trim()?.takeIf { it.isNotBlank() }
    val attendeePhone = contactRequest?.phone?.trim()?.takeIf { it.isNotBlank() }
    val attendeeComment = contactRequest?.comment?.trim()?.takeIf { it.isNotBlank() }
    val chatHint = attendeeName

    AlertDialog(
        onDismissRequest = onDismiss,
        confirmButton = {
            EventTextAction(
                text = strings.close,
                onClick = onDismiss
            )
        },
        dismissButton = {
            EventTextAction(
                text = strings.openChat,
                leadingIcon = Icons.Default.Send,
                onClick = {
                    onStartChatWithUser(booking.clientId.toString(), chatHint)
                    onDismiss()
                }
            )
        },
        title = { Text(strings.bookingDetailsTitle) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Text("${strings.slotBookedByLabel}: $attendeeName", style = MaterialTheme.typography.bodyMedium)
                attendeeFirstName?.let { firstName ->
                    Text(
                        text = "${strings.firstName}: $firstName",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                attendeeEmail?.let { email ->
                    EmailLinkText(
                        text = "${strings.email}: $email",
                        email = email,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                attendeePhone?.let { phone ->
                    PhoneLinkText(
                        text = "${strings.phone}: $phone",
                        phone = phone,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                attendeeComment?.let { comment ->
                    Text(
                        text = "${strings.comment}: $comment",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                Text(strings.bookingStatusText(booking.status), style = MaterialTheme.typography.bodySmall)
                booking.clientNotes?.takeIf { it.isNotBlank() }?.let { notes ->
                    Text("${strings.clientNotesLabel}: $notes", style = MaterialTheme.typography.bodySmall)
                }
                Divider()
                sortedSlots.forEach { slot ->
                    Text(
                        text = "${strings.formatSelectedDate(slot.startTime.toLocalDateCompat())} • ${formatSlotRange(slot)}",
                        style = MaterialTheme.typography.bodySmall
                    )
                }
            }
        }
    )
}

@Composable
private fun BookingRow(
    enrichedBooking: EnrichedBooking,
    isExpanded: Boolean,
    onToggleExpanded: () -> Unit,
    onStartChatWithUser: (String, String?) -> Unit,
) {
    val strings = strings()
    val booking = enrichedBooking.booking
    val tickets = enrichedBooking.tickets
    val contactRequest = enrichedBooking.contactRequest
    val clientEmail = booking.clientEmail?.trim()?.takeIf { it.isNotBlank() }
    val colorScheme = MaterialTheme.colorScheme

    val clientName = remember(booking) { formatClientDisplayName(booking) }
    val chatHint = clientName
    val createdAt = booking.createdAt?.let { formatInstantDateTime(it) } ?: ""

    val statusColor = when (booking.status) {
        BookingStatus.CONFIRMED -> colorScheme.primary
        BookingStatus.COMPLETED -> colorScheme.secondary
        BookingStatus.CANCELLED, BookingStatus.NO_SHOW -> colorScheme.error
        BookingStatus.PENDING, BookingStatus.RESERVED -> colorScheme.tertiary
        BookingStatus.EXPIRED -> colorScheme.error.copy(alpha = 0.7f)
    }

    Column(modifier = Modifier.fillMaxWidth()) {
        // Collapsed header row
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = clientName,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Medium
                )
                Text(
                    text = createdAt,
                    style = MaterialTheme.typography.bodySmall,
                    color = colorScheme.onSurfaceVariant
                )
            }
            Surface(
                shape = RoundedCornerShape(16.dp),
                color = statusColor.copy(alpha = 0.12f),
                border = BorderStroke(1.dp, statusColor)
            ) {
                Text(
                    text = strings.bookingStatusText(booking.status),
                    modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
                    style = MaterialTheme.typography.labelMedium,
                    color = statusColor
                )
            }
            IconButton(onClick = {
                onStartChatWithUser(booking.clientId.toString(), chatHint)
            }) {
                Icon(Icons.Default.Send, contentDescription = strings.send)
            }
        }

        // Expanded details
        if (isExpanded) {
            Spacer(modifier = Modifier.height(12.dp))

            // Tickets section
            if (tickets.isNotEmpty()) {
                Text(
                    text = strings.ticketInfoLabel,
                    style = MaterialTheme.typography.labelMedium,
                    fontWeight = FontWeight.Bold,
                    color = colorScheme.primary
                )
                Spacer(modifier = Modifier.height(4.dp))
                tickets.forEach { ticket ->
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(vertical = 2.dp),
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Text(
                            text = "#${ticket.ticketNumber}",
                            style = MaterialTheme.typography.bodyMedium
                        )
                        ticket.paidPrice?.let { price ->
                            Text(
                                text = strings.formatPrice(price, null),
                                style = MaterialTheme.typography.bodyMedium,
                                fontWeight = FontWeight.Medium
                            )
                        }
                    }
                }
                Spacer(modifier = Modifier.height(8.dp))
            }

            // Contact request section
            if (contactRequest != null || clientEmail != null) {
                Text(
                    text = strings.contactInfoLabel,
                    style = MaterialTheme.typography.labelMedium,
                    fontWeight = FontWeight.Bold,
                    color = colorScheme.primary
                )
                Spacer(modifier = Modifier.height(4.dp))
                contactRequest?.phone?.takeIf { it.isNotBlank() }?.let { phone ->
                    PhoneLinkText(
                        text = "${strings.phone}: $phone",
                        phone = phone,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
                clientEmail?.let { email ->
                    EmailLinkText(
                        text = "${strings.email}: $email",
                        email = email,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
                contactRequest?.comment?.takeIf { it.isNotBlank() }?.let { comment ->
                    Text(
                        text = "${strings.comment}: $comment",
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
                // Display form data from landing page
                contactRequest?.formData?.let { formData ->
                    if (formData.isNotEmpty()) {
                        Spacer(modifier = Modifier.height(4.dp))
                        FormDataDisplay(formData = formData)
                    }
                }
                Spacer(modifier = Modifier.height(8.dp))
            }

            // Time slots (for slot booking)
            if (booking.timeSlots.isNotEmpty()) {
                val sortedSlots = booking.timeSlots.sortedBy { it.startTime }
                sortedSlots.forEach { slot ->
                    val slotDate = strings.formatSelectedDate(slot.startTime.toLocalDateCompat())
                    Text(
                        text = "$slotDate • ${formatSlotRange(slot)}",
                        style = MaterialTheme.typography.bodySmall,
                        color = colorScheme.onSurfaceVariant
                    )
                }
                Spacer(modifier = Modifier.height(8.dp))
            }

            // Occurrence date (for weekly)
            booking.occurrenceDateTime?.let { occurrenceInstant ->
                val occurrenceDate = occurrenceInstant.toLocalDateCompat()
                Text(
                    text = strings.formatSelectedDate(occurrenceDate),
                    style = MaterialTheme.typography.bodySmall,
                    color = colorScheme.onSurfaceVariant
                )
                Spacer(modifier = Modifier.height(8.dp))
            }

            // Client notes
            booking.clientNotes?.takeIf { it.isNotBlank() }?.let { notes ->
                Text(
                    text = strings.clientNotesLabel,
                    style = MaterialTheme.typography.labelMedium,
                    fontWeight = FontWeight.Bold,
                    color = colorScheme.primary
                )
                Text(
                    text = notes,
                    style = MaterialTheme.typography.bodySmall,
                    color = colorScheme.onSurfaceVariant
                )
            }
        }

        // Expand/collapse button
        EventTextAction(
            text = if (isExpanded) strings.hideDetailsBooking else strings.detailsButton,
            onClick = onToggleExpanded,
            trailingIcon = if (isExpanded) Icons.Default.KeyboardArrowUp else Icons.Default.KeyboardArrowDown,
            modifier = Modifier.align(Alignment.End)
        )
    }
}

@Composable
private fun BookingsSection(
    organizerData: OrganizerDataState?,
    eventId: String,
    isSlotBooking: Boolean,
    isRecurring: Boolean,
    selectedDate: LocalDate?,
    onStartChatWithUser: (String, String?) -> Unit,
) {
    val strings = strings()
    val allBookings = organizerData?.enrichedBookings.orEmpty()
    val isLoading = organizerData?.isLoading ?: true
    val error = organizerData?.error

    val isOneTimeEvent = !isSlotBooking && !isRecurring
    val showFilters = !isOneTimeEvent

    var filterMode by remember { mutableStateOf(BookingsFilterMode.TODAY) }
    var expandedBookingId by remember { mutableStateOf<Long?>(null) }
    var currentPage by remember { mutableStateOf(0) }
    val today = remember { currentLocalDate() }
    val pageSize = 10

    // Filter bookings based on filter mode
    val filteredBookings = remember(allBookings, filterMode, selectedDate, today) {
        val filtered = when {
            isOneTimeEvent -> allBookings
            isSlotBooking -> when (filterMode) {
                BookingsFilterMode.ALL -> allBookings
                BookingsFilterMode.TODAY -> allBookings.filter { eb ->
                    eb.booking.timeSlots.any { it.startTime.toLocalDateCompat() == today }
                }
                BookingsFilterMode.THIS_MONTH -> allBookings.filter { eb ->
                    eb.booking.timeSlots.any { slot ->
                        val date = slot.startTime.toLocalDateCompat()
                        date.year == today.year && date.monthNumberCompat() == today.monthNumberCompat()
                    }
                }
                BookingsFilterMode.SELECTED_DATE -> if (selectedDate != null) {
                    allBookings.filter { eb ->
                        eb.booking.timeSlots.any { it.startTime.toLocalDateCompat() == selectedDate }
                    }
                } else allBookings
            }
            // Recurring (weekly/monthly)
            else -> when (filterMode) {
                BookingsFilterMode.ALL -> allBookings
                BookingsFilterMode.TODAY -> allBookings.filter { eb ->
                    eb.booking.occurrenceDateTime?.toLocalDateCompat() == today
                }
                BookingsFilterMode.THIS_MONTH -> allBookings.filter { eb ->
                    eb.booking.occurrenceDateTime?.let { occ ->
                        val date = occ.toLocalDateCompat()
                        date.year == today.year && date.monthNumberCompat() == today.monthNumberCompat()
                    } ?: false
                }
                BookingsFilterMode.SELECTED_DATE -> if (selectedDate != null) {
                    allBookings.filter { eb ->
                        eb.booking.occurrenceDateTime?.toLocalDateCompat() == selectedDate
                    }
                } else allBookings
            }
        }
        filtered.sortedByDescending { it.booking.createdAt?.toEpochMilliseconds() ?: Long.MIN_VALUE }
    }
    val totalCount = filteredBookings.size
    val totalPages = remember(totalCount) { maxOf(1, (totalCount + pageSize - 1) / pageSize) }
    val pageBookings = remember(filteredBookings, currentPage) {
        filteredBookings.drop(currentPage * pageSize).take(pageSize)
    }
    val chipColors = eventFilterChipColors()

    LaunchedEffect(filterMode, selectedDate) {
        currentPage = 0
    }

    LaunchedEffect(totalPages) {
        if (currentPage > totalPages - 1) {
            currentPage = totalPages - 1
        }
    }

    EventSectionCard {
            // Title with count
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = "${strings.bookingsTitle} (${filteredBookings.size})",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = EventCardAccentDark
                )
                organizerData?.refresh?.let { refresh ->
                    IconButton(
                        onClick = refresh,
                        colors = IconButtonDefaults.iconButtonColors(contentColor = EventCardAccentDark)
                    ) {
                        Icon(Icons.Default.Refresh, contentDescription = strings.retry)
                    }
                }
            }

            // Filter chips (only for weekly/slot events)
            if (showFilters) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .horizontalScroll(rememberScrollState()),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    FilterChip(
                        selected = filterMode == BookingsFilterMode.TODAY,
                        onClick = { filterMode = BookingsFilterMode.TODAY },
                        label = { Text(strings.showToday) },
                        colors = chipColors,
                        border = eventFilterChipBorder(selected = filterMode == BookingsFilterMode.TODAY)
                    )
                    FilterChip(
                        selected = filterMode == BookingsFilterMode.THIS_MONTH,
                        onClick = { filterMode = BookingsFilterMode.THIS_MONTH },
                        label = { Text(strings.showThisMonth) },
                        colors = chipColors,
                        border = eventFilterChipBorder(selected = filterMode == BookingsFilterMode.THIS_MONTH)
                    )
                    FilterChip(
                        selected = filterMode == BookingsFilterMode.ALL,
                        onClick = { filterMode = BookingsFilterMode.ALL },
                        label = { Text(strings.showAllBookings) },
                        colors = chipColors,
                        border = eventFilterChipBorder(selected = filterMode == BookingsFilterMode.ALL)
                    )
                    if (selectedDate != null) {
                        FilterChip(
                            selected = filterMode == BookingsFilterMode.SELECTED_DATE,
                            onClick = { filterMode = BookingsFilterMode.SELECTED_DATE },
                            label = { Text(strings.formatSelectedDate(selectedDate)) },
                            colors = chipColors,
                            border = eventFilterChipBorder(selected = filterMode == BookingsFilterMode.SELECTED_DATE)
                        )
                    }
                }
            }

            // Content
            when {
                isLoading -> {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(100.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator(modifier = Modifier.size(32.dp))
                    }
                }
                error != null -> {
                    Text(
                        text = error,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
                filteredBookings.isEmpty() -> {
                    Text(
                        text = strings.totalCount(totalCount),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(
                        text = strings.noBookings,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                else -> {
                    Column(verticalArrangement = Arrangement.spacedBy(0.dp)) {
                        Text(
                            text = strings.totalCount(totalCount),
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.padding(bottom = 8.dp)
                        )
                        pageBookings.forEachIndexed { index, enrichedBooking ->
                            BookingRow(
                                enrichedBooking = enrichedBooking,
                                isExpanded = expandedBookingId == enrichedBooking.booking.id,
                                onToggleExpanded = {
                                    expandedBookingId = if (expandedBookingId == enrichedBooking.booking.id) {
                                        null
                                    } else {
                                        enrichedBooking.booking.id
                                    }
                                },
                                onStartChatWithUser = onStartChatWithUser
                            )
                            if (index < pageBookings.lastIndex) {
                                Divider(
                                    modifier = Modifier.padding(vertical = 12.dp),
                                    color = EventCardOutline
                                )
                            }
                        }
                        if (totalPages > 1) {
                            Spacer(modifier = Modifier.height(8.dp))
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                EventTextAction(
                                    text = strings.prev,
                                    onClick = { if (currentPage > 0) currentPage -= 1 },
                                    enabled = currentPage > 0
                                )
                                Text(
                                    text = "${currentPage + 1} / $totalPages",
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                                EventTextAction(
                                    text = strings.next,
                                    onClick = { if (currentPage + 1 < totalPages) currentPage += 1 },
                                    enabled = currentPage + 1 < totalPages
                                )
                            }
                        }
                    }
                }
            }
        }
    }
@Composable
private fun UnconfirmedRequestsSection(
    requests: List<ContactApi.ContactRequestDto>,
    isLoading: Boolean,
    error: String?,
    onStartChatWithUser: (String, String?) -> Unit,
    onRefresh: () -> Unit,
) {
    val strings = strings()

    if (requests.isEmpty() && !isLoading && error == null) {
        return // Don't show section if empty
    }

    EventSectionCard {
        // Title with count
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = "${strings.unconfirmedRequestsTitle} (${requests.size})",
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = EventCardAccentDark
            )
            IconButton(
                onClick = onRefresh,
                colors = IconButtonDefaults.iconButtonColors(contentColor = EventCardAccentDark)
            ) {
                Icon(Icons.Default.Refresh, contentDescription = strings.retry)
            }
        }

        when {
            isLoading -> {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(80.dp),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator(modifier = Modifier.size(32.dp))
                }
            }
            error != null -> {
                Text(
                    text = error,
                    color = MaterialTheme.colorScheme.error,
                    style = MaterialTheme.typography.bodyMedium
                )
            }
            requests.isEmpty() -> {
                Text(
                    text = strings.noUnconfirmedRequests,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            else -> {
                Column(verticalArrangement = Arrangement.spacedBy(0.dp)) {
                    requests.forEachIndexed { index, request ->
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            Column(modifier = Modifier.weight(1f)) {
                                request.phone?.takeIf { it.isNotBlank() }?.let { phone ->
                                    PhoneLinkText(
                                        text = phone,
                                        phone = phone,
                                        style = MaterialTheme.typography.titleMedium
                                    )
                                }
                                request.comment?.takeIf { it.isNotBlank() }?.let { comment ->
                                    Text(
                                        text = comment,
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                                        maxLines = 2,
                                        overflow = TextOverflow.Ellipsis
                                    )
                                }
                                // Display form data from landing page
                                request?.formData?.let { formData ->
                                    if (formData.isNotEmpty()) {
                                        Spacer(modifier = Modifier.height(4.dp))
                                        FormDataDisplay(formData = formData)
                                    }
                                }
                                request.createdAt?.let { createdAt ->
                                    Text(
                                        text = formatDateTime(createdAt),
                                        style = MaterialTheme.typography.bodySmall,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                }
                            }
                            request.userId?.let { userId ->
                                IconButton(
                                    onClick = { onStartChatWithUser(userId.toString(), request.phone) },
                                    colors = IconButtonDefaults.iconButtonColors(contentColor = EventCardAccentDark)
                                ) {
                                    Icon(Icons.Default.Send, contentDescription = strings.send)
                                }
                            }
                        }
                        if (index < requests.lastIndex) {
                            Divider(
                                modifier = Modifier.padding(vertical = 8.dp),
                                color = EventCardOutline
                            )
                        }
                    }
                }
            }
        }
    }
}

private fun formatInstantDateTime(instant: Instant): String {
    return try {
        val dt = instant.toLocalDateTimeInTimeZone(getLocalTimeZone().displayId)
        val dd = dt.dayOfMonth.toString().padStart(2, '0')
        val mm = dt.monthNumber.toString().padStart(2, '0')
        val yyyy = dt.year.toString()
        val hh = dt.hour.toString().padStart(2, '0')
        val min = dt.minute.toString().padStart(2, '0')
        "$dd.$mm.$yyyy $hh:$min"
    } catch (e: Exception) {
        ""
    }
}

private fun formatDateTime(iso: String?): String {
    if (iso.isNullOrBlank()) return ""
    return try {
        val dt = kotlinx.datetime.LocalDateTime.parse(iso)
        val dd = dt.date.dayOfMonth.toString().padStart(2, '0')
        val mm = dt.date.monthNumberCompat().toString().padStart(2, '0')
        val yyyy = dt.date.year.toString()
        val hh = dt.hour.toString().padStart(2, '0')
        val min = dt.minute.toString().padStart(2, '0')
        "$dd.$mm.$yyyy $hh:$min"
    } catch (e: Exception) {
        // Fallback: trim seconds
        iso.take(16).replace('T', ' ')
    }
}

@Composable
private fun SimpleFreeBookingButton(
    eventId: String,
    onNavigateToTicket: (String) -> Unit = {},
    bookingEnabled: Boolean = true
) {
    val strings = strings()
    val scope = rememberCoroutineScope()
    val bookingApi = remember { BookingApi() }
    var isBooking by remember { mutableStateOf(false) }
    var bookingError by remember { mutableStateOf<String?>(null) }
    var booked by remember { mutableStateOf(false) }

    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        bookingError?.let { error ->
            Text(
                text = error,
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodySmall
            )
        }

        if (booked) {
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(containerColor = Color(0xFFE8F5E9)),
                shape = RoundedCornerShape(12.dp)
            ) {
                Row(
                    modifier = Modifier.padding(16.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        Icons.Default.CheckCircle,
                        contentDescription = null,
                        tint = Color(0xFF4CAF50),
                        modifier = Modifier.size(24.dp)
                    )
                    Spacer(modifier = Modifier.width(12.dp))
                    Text(
                        text = strings.bookingSuccess,
                        style = MaterialTheme.typography.bodyLarge,
                        color = Color(0xFF2E7D32)
                    )
                }
            }
        } else {
            Button(
                onClick = {
                    if (isBooking) return@Button
                    isBooking = true
                    bookingError = null
                    scope.launch {
                        bookingApi.bookEventTickets(
                            EventTicketBookingRequest(
                                eventId = eventId,
                                quantity = 1,
                                holderName = null,
                                holderEmail = null
                            )
                        )
                            .onSuccess { result ->
                                booked = true
                                when (result) {
                                    is EventBookingResult.TicketsIssued -> {
                                        result.tickets.firstOrNull()?.id?.let { onNavigateToTicket(it) }
                                    }
                                    is EventBookingResult.Reservation -> {
                                        // For paid events - should not happen for free booking
                                    }
                                }
                            }
                            .onFailure { error ->
                                bookingError = error.message ?: strings.bookingFailed
                            }
                        isBooking = false
                    }
                },
                modifier = Modifier
                    .fillMaxWidth()
                    .height(56.dp)
                    .testTag(TestTags.EventCard.FREE_CONTACT_SUBMIT),
                  enabled = bookingEnabled && !isBooking && !booked,
                colors = ButtonDefaults.buttonColors(
                    containerColor = EventCardAccent,
                    contentColor = Color.White
                ),
                shape = RoundedCornerShape(12.dp)
            ) {
                if (isBooking) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(24.dp),
                        color = Color.White,
                        strokeWidth = 2.dp
                    )
                } else {
                    Icon(Icons.Default.EventAvailable, contentDescription = null)
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = strings.bookFree,
                        style = MaterialTheme.typography.titleMedium
                    )
                }
            }
        }
    }
}

@Composable
private fun PaidEventContactSection(
    eventId: String,
    quantity: Int,
    maxQuantity: Int?,
    onQuantityChange: (Int) -> Unit,
    effectivePrice: Double,
    currency: String?,
    onBookClick: () -> Unit,
    bookingError: String?,
    bookingId: Long? = null,
    platformFee: PlatformFee = PlatformFee.DEFAULT,
    bookingEnabled: Boolean = true
) {
    val strings = strings()
    val scope = rememberCoroutineScope()
    val contactApi = remember { ContactApi() }
    var contactPhone by remember { mutableStateOf("") }
    var contactComment by remember { mutableStateOf("") }
    var contactPhoneError by remember { mutableStateOf<String?>(null) }
    var contactFormError by remember { mutableStateOf<String?>(null) }
    var contactSubmissionError by remember { mutableStateOf<String?>(null) }
    var contactSubmitted by remember { mutableStateOf(false) }
    var isSubmittingContact by remember { mutableStateOf(false) }
    var contactRequestId by remember { mutableStateOf<Long?>(null) }
    var bookingIdLinked by remember { mutableStateOf(false) }

    // Update contact request with bookingId when booking is successful
    LaunchedEffect(bookingId, contactRequestId) {
        if (bookingId != null && contactRequestId != null && !bookingIdLinked) {
            contactApi.updateBookingId(eventId, contactRequestId!!, bookingId)
                .onSuccess { bookingIdLinked = true }
        }
    }

    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        // Contact form
        Text(
            text = strings.contactInfo,
            style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.SemiBold)
        )

        OutlinedTextField(
            value = contactPhone,
            onValueChange = {
                contactPhone = it
                contactPhoneError = if (it.isBlank()) null else if (isValidPhone(it)) null else strings.invalidPhone
                contactFormError = null
                contactSubmissionError = null
                contactSubmitted = false
            },
            label = { Text(strings.phone) },
            isError = contactPhoneError != null,
            supportingText = {
                contactPhoneError?.let { error ->
                    Text(text = error, color = MaterialTheme.colorScheme.error)
                }
            },
            singleLine = true,
            shape = RoundedCornerShape(EventCardInputCorner),
            colors = eventOutlinedFieldColors(),
            modifier = Modifier
                .fillMaxWidth()
                .testTag(TestTags.EventCard.FREE_CONTACT_PHONE)
        )

        OutlinedTextField(
            value = contactComment,
            onValueChange = {
                contactComment = it
                contactFormError = null
                contactSubmissionError = null
                contactSubmitted = false
            },
            label = { Text(strings.comment) },
            singleLine = false,
            minLines = 2,
            maxLines = 5,
            shape = RoundedCornerShape(EventCardInputCorner),
            colors = eventOutlinedFieldColors(),
            modifier = Modifier
                .fillMaxWidth()
                .testTag(TestTags.EventCard.FREE_CONTACT_COMMENT)
        )

        contactFormError?.let { error ->
            Text(text = error, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall)
        }
        contactSubmissionError?.let { error ->
            Text(text = error, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall)
        }

        if (contactSubmitted) {
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(containerColor = Color(0xFFE8F5E9)),
                shape = RoundedCornerShape(8.dp)
            ) {
                Row(
                    modifier = Modifier.padding(12.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(Icons.Default.CheckCircle, contentDescription = null, tint = Color(0xFF4CAF50))
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(strings.contactSubmitted, color = Color(0xFF2E7D32))
                }
            }
        }

        Spacer(modifier = Modifier.height(8.dp))

        // Ticket quantity selector
        TicketQuantitySelector(
            quantity = quantity,
            maxQuantity = maxQuantity,
            available = maxQuantity,
            onQuantityChange = onQuantityChange,
            decreaseButtonTag = TestTags.EventCard.ONE_TIME_QUANTITY_DECREASE,
            increaseButtonTag = TestTags.EventCard.ONE_TIME_QUANTITY_INCREASE,
            valueTag = TestTags.EventCard.ONE_TIME_QUANTITY_VALUE
        )

        Spacer(modifier = Modifier.height(8.dp))

        // Book button
        val ticketTotal = effectivePrice * quantity
        val serviceFee = platformFee.calculateFee(ticketTotal)
        val grandTotal = ticketTotal + serviceFee

        // Price breakdown
        if (platformFee.enabled && serviceFee > 0) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 4.dp, vertical = 8.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text(
                        text = if (quantity > 1) {
                            "${strings.formatPrice(effectivePrice, currency)} x $quantity"
                        } else {
                            strings.costLabel
                        },
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Text(
                        text = strings.formatPrice(ticketTotal, currency),
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
                Spacer(modifier = Modifier.height(4.dp))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text(
                        text = strings.serviceFeePercent((platformFee.feePercent * 100).toInt()),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Text(
                        text = strings.formatPrice(serviceFee, currency),
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
                Spacer(modifier = Modifier.height(4.dp))
                HorizontalDivider()
                Spacer(modifier = Modifier.height(4.dp))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text(
                        text = strings.totalAmount,
                        style = MaterialTheme.typography.bodyMedium,
                        fontWeight = FontWeight.Bold
                    )
                    Text(
                        text = strings.formatPrice(grandTotal, currency),
                        style = MaterialTheme.typography.bodyMedium,
                        fontWeight = FontWeight.Bold
                    )
                }
            }
            Spacer(modifier = Modifier.height(8.dp))
        }

        Button(
            onClick = {
                if (!bookingEnabled || isSubmittingContact) return@Button
                // Validate contact form first
                val validPhone = contactPhone.isNotBlank() && isValidPhone(contactPhone)
                contactPhoneError = if (contactPhone.isBlank()) strings.phoneRequired else if (!validPhone) strings.invalidPhone else null

                if (!validPhone) {
                    contactFormError = strings.checkContacts
                    return@Button
                }

                if (!contactSubmitted && !isSubmittingContact) {
                    // Submit contact first, then proceed to booking
                    contactFormError = null
                    contactSubmissionError = null
                    isSubmittingContact = true
                    scope.launch {
                        contactApi.submitContact(
                            eventId = eventId,
                            phone = contactPhone.takeIf { it.isNotBlank() },
                            comment = contactComment.takeIf { it.isNotBlank() }
                        )
                            .onSuccess { contactRequest ->
                                contactSubmitted = true
                                contactRequestId = contactRequest.id
                                onBookClick()
                            }
                            .onFailure { error ->
                                contactSubmitted = false
                                contactSubmissionError = error.message ?: strings.contactSubmitting
                            }
                        isSubmittingContact = false
                    }
                } else if (contactSubmitted) {
                    onBookClick()
                }
            },
            modifier = Modifier
                .fillMaxWidth()
                .height(64.dp)
                .testTag(TestTags.EventCard.ONE_TIME_BOOK_BUTTON),
            enabled = bookingEnabled && !isSubmittingContact,
            colors = ButtonDefaults.buttonColors(
                containerColor = EventCardAccent,
                contentColor = Color.White
            ),
            shape = MaterialTheme.shapes.medium
        ) {
            if (isSubmittingContact) {
                CircularProgressIndicator(
                    modifier = Modifier.size(24.dp),
                    color = Color.White,
                    strokeWidth = 2.dp
                )
            } else {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = "${strings.costLabel} ${strings.formatPrice(grandTotal, currency)}",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold
                    )
                    if (quantity > 1) {
                        Text(
                            text = "x$quantity",
                            style = MaterialTheme.typography.bodyLarge,
                            fontWeight = FontWeight.SemiBold
                        )
                    }
                    Icon(
                        imageVector = Icons.Default.ConfirmationNumber,
                        contentDescription = null,
                        modifier = Modifier.size(32.dp)
                    )
                }
            }
        }

        bookingError?.let { error ->
            Text(text = error, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodyMedium)
        }
    }
}

@Composable
private fun FreeContactBookingSection(
    eventId: String,
    onNavigateToTicket: (String) -> Unit = {},
    bookingEnabled: Boolean = true
) {
    val strings = strings()
    var phone by remember { mutableStateOf("") }
    var comment by remember { mutableStateOf("") }
    var phoneError by remember { mutableStateOf<String?>(null) }
    var formError by remember { mutableStateOf<String?>(null) }
    var isSubmitting by remember { mutableStateOf(false) }
    var submitted by remember { mutableStateOf(false) }
    var createdTicketId by remember { mutableStateOf<String?>(null) }
    var bookingError by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()
    val contactApi = remember { ContactApi() }
    val bookingApi = remember { BookingApi() }

    LaunchedEffect(createdTicketId) {
        createdTicketId?.let { ticketId ->
            onNavigateToTicket(ticketId)
            createdTicketId = null
        }
    }

    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        OutlinedTextField(
            value = phone,
            onValueChange = {
                phone = it
                formError = null
                phoneError = if (it.isBlank()) null else if (isValidPhone(it)) null else strings.invalidPhone
            },
            label = { Text(strings.phone) },
            isError = phoneError != null,
            supportingText = { phoneError?.let { Text(it, color = MaterialTheme.colorScheme.error) } },
            singleLine = true,
            enabled = bookingEnabled && !submitted,
            shape = RoundedCornerShape(EventCardInputCorner),
            colors = eventOutlinedFieldColors(),
            modifier = Modifier
                .fillMaxWidth()
                .testTag(TestTags.EventCard.FREE_CONTACT_PHONE)
        )
        OutlinedTextField(
            value = comment,
            onValueChange = {
                comment = it
                formError = null
            },
            label = { Text(strings.comment) },
            singleLine = false,
            minLines = 2,
            maxLines = 5,
            enabled = bookingEnabled && !submitted,
            shape = RoundedCornerShape(EventCardInputCorner),
            colors = eventOutlinedFieldColors(),
            modifier = Modifier
                .fillMaxWidth()
                .testTag(TestTags.EventCard.FREE_CONTACT_COMMENT)
        )

        Button(
            onClick = {
                if (!bookingEnabled || isSubmitting) return@Button
                val validPhone = phone.isNotBlank() && isValidPhone(phone)
                phoneError = if (phone.isBlank()) strings.phoneRequired else if (!validPhone) strings.invalidPhone else null
                if (!validPhone) {
                    formError = strings.checkContacts
                    return@Button
                }

                formError = null
                isSubmitting = true
                submitted = false
                bookingError = null
                scope.launch {
                    contactApi.submitContact(
                        eventId,
                        phone.takeIf { it.isNotBlank() },
                        comment.takeIf { it.isNotBlank() }
                    )
                        .onSuccess { contactRequest ->
                            submitted = true
                            bookingApi.bookEventTickets(
                                EventTicketBookingRequest(
                                    eventId = eventId,
                                    quantity = 1,
                                    holderName = null,
                                    holderEmail = null
                                )
                            )
                                .onSuccess { result ->
                                    when (result) {
                                        is EventBookingResult.TicketsIssued -> {
                                            val ticket = result.tickets.firstOrNull()
                                            createdTicketId = ticket?.id
                                            ticket?.bookingId?.toLongOrNull()?.let { bookingId ->
                                                contactApi.updateBookingId(eventId, contactRequest.id, bookingId)
                                            }
                                        }
                                        is EventBookingResult.Reservation -> {
                                            bookingError = strings.unexpectedReservation
                                        }
                                    }
                                }
                                .onFailure { error ->
                                    bookingError = userFriendlyErrorMessage(
                                        error,
                                        strings.bookingRequestFailed,
                                    )
                                }
                        }
                        .onFailure {
                            submitted = false
                            bookingError = userFriendlyErrorMessage(
                                it,
                                strings.contactSendFailed,
                            )
                        }
                    isSubmitting = false
                }
            },
            modifier = Modifier
                .fillMaxWidth()
                .height(56.dp),
            enabled = bookingEnabled && !submitted && !isSubmitting,
            colors = ButtonDefaults.buttonColors(
                containerColor = EventCardAccent,
                contentColor = Color.White
            ),
            shape = MaterialTheme.shapes.medium
        ) {
            if (isSubmitting) {
                CircularProgressIndicator(
                    modifier = Modifier.size(24.dp),
                    color = Color.White,
                    strokeWidth = 2.dp
                )
            } else {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = strings.bookFree,
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold
                    )
                    Icon(
                        imageVector = Icons.Default.ConfirmationNumber,
                        contentDescription = null,
                        modifier = Modifier.size(32.dp)
                    )
                }
            }
        }

        bookingError?.let { error ->
            Text(text = error, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodyMedium)
        }
    }
}

private fun isValidPhone(input: String): Boolean {
    val digits = input.filter { it.isDigit() }
    return digits.length in 7..15
}

private fun isValidEmail(input: String): Boolean {
    // <local>@<domain>.<tld>
    val emailRegex = """^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$""".toRegex()
    return emailRegex.matches(input)
}

@Composable
private fun SlotBookingSection(
    eventId: String,
    event: Event,
    state: SlotBookingUiState,
    onPreviousMonth: () -> Unit,
    onNextMonth: () -> Unit,
    onDateSelected: (LocalDate) -> Unit,
    onSlotToggle: (Long) -> Unit,
    onBookClick: () -> Unit,
    isOrganizer: Boolean = false,
    organizerBookings: List<Booking> = emptyList(),
    bookingPhonesById: Map<Long, String> = emptyMap(),
    onShowBookingDetails: (Booking) -> Unit = {},
    onStartChatWithUser: (String, String?) -> Unit = { _, _ -> },
    onRequestCancelSlots: () -> Unit = {},
    onRequestRestoreSlots: () -> Unit = {},
    onRequestCancelDay: () -> Unit = {},
    onRequestRestoreDay: () -> Unit = {},
    requireContactInfo: Boolean = false,
    eventTimeZone: String? = null,
    platformFee: PlatformFee = PlatformFee.DEFAULT,
    bookingEnabled: Boolean = true,
) {
    val strings = strings()
    val monthStart = state.calendarMonthStart ?: return
    val today = remember { currentLocalDate() }
    val userTimeZoneId = remember { getLocalTimeZone().displayId }
    val showUserTimezone = eventTimeZone != null && eventTimeZone != userTimeZoneId
    val monthTitle = "${strings.monthNameFull(monthStart.monthNumberCompat())} ${monthStart.year}"
    val weekDays = (0 until 7).map(strings::weekdayShortName)
    val currentMonthStart = LocalDate(year = today.year, monthNumber = today.monthNumberCompat(), dayOfMonth = 1)
    val canGoPrevious = monthStart > currentMonthStart
    val bookingsBySlotId = remember(organizerBookings) {
        organizerBookings
            .flatMap { booking -> booking.timeSlots.map { it.id to booking } }
            .toMap()
    }
    val shouldCollectContacts = requireContactInfo && !isOrganizer
    val scope = rememberCoroutineScope()
    val contactApi = remember { ContactApi() }
    var contactPhone by remember { mutableStateOf("") }
    var contactComment by remember { mutableStateOf("") }
    var contactPhoneError by remember { mutableStateOf<String?>(null) }
    var contactFormError by remember { mutableStateOf<String?>(null) }
    var contactSubmissionError by remember { mutableStateOf<String?>(null) }
    var contactSubmitted by remember { mutableStateOf(false) }
    var isSubmittingContact by remember { mutableStateOf(false) }
    var contactRequestId by remember { mutableStateOf<Long?>(null) }
    var bookingIdLinked by remember { mutableStateOf(false) }

    // Update contact request with bookingId when booking is successful
    LaunchedEffect(state.lastBookingId, contactRequestId) {
        if (state.lastBookingId != null && contactRequestId != null && !bookingIdLinked) {
            contactApi.updateBookingId(eventId, contactRequestId!!, state.lastBookingId)
                .onSuccess { bookingIdLinked = true }
        }
    }

    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        Text(
            text = strings.slotBooking,
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.Bold
        )

        Card(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(20.dp),
            colors = CardDefaults.cardColors(containerColor = EventCardSurface),
            border = BorderStroke(1.dp, EventCardOutline),
            elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
        ) {
            Column(
                modifier = Modifier.padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    CalendarNavButton(
                        onClick = onPreviousMonth,
                        enabled = canGoPrevious,
                        icon = Icons.Default.ChevronLeft,
                        contentDescription = strings.previousMonth
                    )
                    Text(
                        text = monthTitle,
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        color = EventCardAccentDark
                    )
                    CalendarNavButton(
                        onClick = onNextMonth,
                        enabled = true,
                        icon = Icons.Default.ChevronRight,
                        contentDescription = strings.nextMonth
                    )
                }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    weekDays.forEach { day ->
                        Text(
                            text = day,
                            modifier = Modifier.weight(1f),
                            textAlign = TextAlign.Center,
                            style = MaterialTheme.typography.labelMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                }

                if (isOrganizer) {
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            Button(
                                onClick = onRequestCancelDay,
                                enabled = state.selectedDate != null && !state.isCancellingDay,
                                modifier = Modifier
                                    .weight(1f)
                                    .testTag(TestTags.EventCard.SLOT_CANCEL_DAY_BUTTON),
                                colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error),
                                shape = RoundedCornerShape(12.dp)
                            ) {
                                if (state.isCancellingDay) {
                                    CircularProgressIndicator(
                                        modifier = Modifier.size(18.dp),
                                        strokeWidth = 2.dp,
                                        color = MaterialTheme.colorScheme.onError
                                    )
                                } else {
                                    Icon(
                                        imageVector = Icons.Default.EventBusy,
                                        contentDescription = null,
                                        modifier = Modifier.size(20.dp)
                                    )
                                    Spacer(modifier = Modifier.width(8.dp))
                                    Text(strings.cancelSlotDayAction)
                                }
                            }
                            OutlinedButton(
                                onClick = onRequestRestoreDay,
                                enabled = state.selectedDate != null && !state.isRestoringDay,
                                modifier = Modifier
                                    .weight(1f)
                                    .testTag(TestTags.EventCard.SLOT_RESTORE_DAY_BUTTON),
                                shape = RoundedCornerShape(12.dp),
                                border = BorderStroke(1.dp, EventCardAccent),
                                colors = ButtonDefaults.outlinedButtonColors(
                                    contentColor = EventCardAccentDark
                                )
                            ) {
                                if (state.isRestoringDay) {
                                    CircularProgressIndicator(
                                        modifier = Modifier.size(18.dp),
                                        strokeWidth = 2.dp
                                    )
                                } else {
                                    Icon(
                                        imageVector = Icons.Default.Restore,
                                        contentDescription = null,
                                        modifier = Modifier.size(20.dp)
                                    )
                                    Spacer(modifier = Modifier.width(8.dp))
                                    Text(strings.restoreSlotDayAction)
                                }
                            }
                        }
                        state.dayCancellationError?.let { error ->
                            Text(
                                text = error,
                                color = MaterialTheme.colorScheme.error,
                                style = MaterialTheme.typography.bodyMedium
                            )
                        }
                        state.restoreDayError?.let { error ->
                            Text(
                                text = error,
                                color = MaterialTheme.colorScheme.error,
                                style = MaterialTheme.typography.bodyMedium
                            )
                        }
                    }
                }

                val daysInMonth = monthStart.daysUntil(monthStart.plus(DatePeriod(months = 1)))
                val leadingEmpty = monthStart.dayOfWeek.isoDayNumber - 1
                val totalCells = leadingEmpty + daysInMonth
                val trailingEmpty = (7 - totalCells % 7) % 7
                val calendarDays = buildList<LocalDate?> {
                    repeat(leadingEmpty) { add(null) }
                    repeat(daysInMonth) { index ->
                        val date = monthStart.plus(DatePeriod(days = index))
                        add(if (date < today) null else date)
                    }
                    repeat(trailingEmpty) { add(null) }
                }

                calendarDays.chunked(7).forEach { week ->
                    Row(modifier = Modifier.fillMaxWidth()) {
                        week.forEach { date ->
                            if (date == null) {
                                Spacer(
                                    modifier = Modifier
                                        .weight(1f)
                                        .height(48.dp)
                                )
                            } else {
                                val isSelected = date == state.selectedDate
                                val availableSlots = state.slotsByDate[date].orEmpty()
                                val hasSlots = availableSlots.isNotEmpty()
                                val isToday = date == today
                                val textColor = when {
                                    isSelected -> Color.White
                                    hasSlots -> EventCardAccentDark
                                    isOrganizer -> MaterialTheme.colorScheme.onSurface
                                    else -> MaterialTheme.colorScheme.onSurfaceVariant
                                }
                                val backgroundColor = when {
                                    isSelected -> EventCardAccent
                                    hasSlots -> EventCardAccentSoft
                                    else -> Color.Transparent
                                }
                                val canSelectDay = if (isOrganizer) {
                                    !state.isLoading
                                } else {
                                    hasSlots && !state.isLoading
                                }

                                Box(
                                    modifier = Modifier
                                        .weight(1f)
                                        .height(48.dp)
                                        .padding(vertical = 4.dp)
                                        .clip(RoundedCornerShape(12.dp))
                                        .background(backgroundColor)
                                        .testTag("${TestTags.EventCard.SLOT_DAY_PREFIX}$date")
                                        .clickable(enabled = canSelectDay) {
                                            onDateSelected(date)
                                        },
                                    contentAlignment = Alignment.Center
                                ) {
                                    Column(
                                        modifier = Modifier.fillMaxSize(),
                                        horizontalAlignment = Alignment.CenterHorizontally,
                                        verticalArrangement = Arrangement.Center
                                    ) {
                                        Text(
                                            text = date.dayOfMonth.toString(),
                                            style = MaterialTheme.typography.bodyMedium,
                                            color = textColor
                                        )
                                        if (hasSlots) {
                                            Box(
                                                modifier = Modifier
                                                    .padding(top = 4.dp)
                                                    .size(6.dp)
                                                    .clip(CircleShape)
                                                    .background(if (isSelected) Color.White else EventCardAccent)
                                            )
                                        }
                                    }

                                    if (isToday && !isSelected) {
                                        Box(
                                            modifier = Modifier
                                                .align(Alignment.TopCenter)
                                                .padding(top = 4.dp)
                                                .width(16.dp)
                                                .height(2.dp)
                                                .background(EventCardWarm, RoundedCornerShape(1.dp))
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        if (state.isLoading) {
            LinearProgressIndicator(
                modifier = Modifier.fillMaxWidth(),
                color = EventCardAccent,
                trackColor = EventCardAccentSoft
            )
        }

        state.error?.let { error ->
            Text(
                text = error,
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodyMedium
            )
        }

        val selectedDate = state.selectedDate
        val availableSlotsForSelectedDay = selectedDate?.let { state.slotsByDate[it] }?.sortedBy { it.startTime }
            ?: emptyList()
        data class SlotDisplayItem(
            val slot: TimeSlot,
            val booking: Booking?,
            val fromBookingList: Boolean,
        )
        val bookingSlotsForSelectedDay = remember(selectedDate, organizerBookings) {
            if (!isOrganizer || selectedDate == null) {
                emptyList()
            } else {
                organizerBookings.flatMap { booking ->
                    booking.timeSlots
                        .filter { it.startTime.toLocalDateCompat() == selectedDate }
                        .map { it to booking }
                }
            }
        }
        val slotItems = remember(selectedDate, availableSlotsForSelectedDay, bookingSlotsForSelectedDay) {
            val items = mutableListOf<SlotDisplayItem>()
            val existingIds = mutableSetOf<Long>()
            availableSlotsForSelectedDay.forEach { slot ->
                existingIds += slot.id
                items += SlotDisplayItem(slot, bookingsBySlotId[slot.id], false)
            }
            bookingSlotsForSelectedDay.forEach { (slot, booking) ->
                if (!existingIds.contains(slot.id)) {
                    items += SlotDisplayItem(slot, booking, true)
                }
            }
            items.sortedBy { it.slot.startTime }
        }

        selectedDate?.let { date ->
            Text(
                text = strings.formatSelectedDate(date),
                style = MaterialTheme.typography.titleSmall,
                fontWeight = FontWeight.SemiBold
            )
        }

        if (!state.isLoading) {
            if (slotItems.isEmpty()) {
                Text(
                    text = strings.slotBookingsEmptyMessage,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            } else {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    // Header row with timezone columns
                    if (showUserTimezone) {
                        val eventTzAbbreviation = eventTimeZone?.let { getTimezoneAbbreviation(it) } ?: ""
                        val userTzAbbreviation = getTimezoneAbbreviation(userTimeZoneId)
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(horizontal = 12.dp, vertical = 4.dp),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(
                                text = eventTzAbbreviation,
                                style = MaterialTheme.typography.labelMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                modifier = Modifier.weight(1f)
                            )
                            Text(
                                text = userTzAbbreviation,
                                style = MaterialTheme.typography.labelMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                modifier = Modifier.weight(1f),
                                textAlign = TextAlign.Center
                            )
                            Text(
                                text = strings.statusLabel,
                                style = MaterialTheme.typography.labelMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                modifier = Modifier.weight(1f),
                                textAlign = TextAlign.End
                            )
                        }
                    }

                    slotItems.forEach { slotItem ->
                        val slot = slotItem.slot
                        val slotRangeTag = formatSlotRange(slot)
                        val slotStatus = slot.status ?: if (slotItem.fromBookingList) TimeSlotStatus.BOOKED else null
                        val bookingForSlot = slotItem.booking
                        val showOrganizerBooking = isOrganizer && bookingForSlot != null
                        val baseDisabled = slotStatus != null && slotStatus != TimeSlotStatus.AVAILABLE
                        val isSelectedSlot = state.selectedSlotIds.contains(slot.id)
                        val cardEnabled = when {
                            showOrganizerBooking -> true
                            else -> !baseDisabled
                        }
                        val onCardClick = when {
                            showOrganizerBooking -> ({ bookingForSlot?.let(onShowBookingDetails) })
                            else -> ({ onSlotToggle(slot.id) })
                        }

                        // Calculate event timezone time for this slot
                        val eventTimezoneSlotRange = if (eventTimeZone != null) {
                            val eventStartTime = slot.startTime.toLocalDateTimeInTimeZone(eventTimeZone)
                            val eventEndTime = slot.endTime.toLocalDateTimeInTimeZone(eventTimeZone)
                            formatSlotRange(eventStartTime, eventEndTime)
                        } else {
                            formatSlotRange(slot)
                        }

                        // Calculate user timezone time for this slot
                        val userTimezoneSlotRange = if (showUserTimezone) {
                            val userStartTime = slot.startTime.toLocalDateTimeInTimeZone(userTimeZoneId)
                            val userEndTime = slot.endTime.toLocalDateTimeInTimeZone(userTimeZoneId)
                            formatSlotRange(userStartTime, userEndTime)
                        } else null

                        Card(
                            modifier = Modifier
                                .fillMaxWidth()
                                .testTag("${TestTags.EventCard.SLOT_ITEM_PREFIX}$slotRangeTag"),
                            shape = RoundedCornerShape(12.dp),
                            colors = CardDefaults.cardColors(
                                containerColor = when {
                                    showOrganizerBooking -> EventCardSurface
                                    isSelectedSlot -> EventCardAccentSoft
                                    else -> EventCardSurface
                                }
                            ),
                            border = when {
                                showOrganizerBooking -> BorderStroke(1.dp, EventCardAccent)
                                isSelectedSlot -> BorderStroke(1.dp, EventCardAccent)
                                else -> null
                            },
                            enabled = cardEnabled && !state.isLoading,
                            onClick = { onCardClick() }
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(12.dp),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                // Event timezone time (left column)
                                Column(
                                    verticalArrangement = Arrangement.spacedBy(4.dp),
                                    modifier = Modifier.weight(1f)
                                ) {
                                    Text(
                                        text = eventTimezoneSlotRange,
                                        style = MaterialTheme.typography.titleMedium,
                                        color = if (!cardEnabled && !showOrganizerBooking) {
                                            MaterialTheme.colorScheme.onSurfaceVariant
                                        } else {
                                            MaterialTheme.colorScheme.onSurface
                                        }
                                    )
                                    slot.notes?.takeIf { it.isNotBlank() }?.let { notes ->
                                        Text(
                                            text = notes,
                                            style = MaterialTheme.typography.bodySmall,
                                            color = MaterialTheme.colorScheme.onSurfaceVariant
                                        )
                                    }
                                    if (showOrganizerBooking) {
                                        val attendeeName = bookingForSlot?.let { formatClientDisplayName(it) }
                                            ?: bookingForSlot?.clientId?.toString().orEmpty()
                                        val attendeeEmail = bookingForSlot?.clientEmail?.trim()?.takeIf { it.isNotBlank() }
                                        Text(
                                            text = "${strings.slotBookedByLabel}: $attendeeName",
                                            style = MaterialTheme.typography.bodySmall,
                                            color = MaterialTheme.colorScheme.onSurfaceVariant
                                        )
                                        attendeeEmail?.let { email ->
                                            EmailLinkText(
                                                text = "${strings.email}: $email",
                                                email = email,
                                                style = MaterialTheme.typography.bodySmall,
                                                color = MaterialTheme.colorScheme.onSurfaceVariant
                                            )
                                        }
                                        bookingForSlot?.id?.let { bookingPhonesById[it] }?.let { phone ->
                                            PhoneLinkText(
                                                text = "${strings.phone}: $phone",
                                                phone = phone,
                                                style = MaterialTheme.typography.bodySmall,
                                                color = MaterialTheme.colorScheme.onSurfaceVariant
                                            )
                                        }
                                        bookingForSlot?.clientNotes?.takeIf { it.isNotBlank() }?.let { notes ->
                                            Text(
                                                text = "${strings.clientNotesLabel}: $notes",
                                                style = MaterialTheme.typography.bodySmall,
                                                color = MaterialTheme.colorScheme.onSurfaceVariant
                                            )
                                        }
                                    }
                                }

                                // User timezone time (center column)
                                if (userTimezoneSlotRange != null) {
                                    Text(
                                        text = userTimezoneSlotRange,
                                        style = MaterialTheme.typography.titleMedium,
                                        color = if (!cardEnabled && !showOrganizerBooking) {
                                            MaterialTheme.colorScheme.onSurfaceVariant
                                        } else {
                                            MaterialTheme.colorScheme.onSurface
                                        },
                                        modifier = Modifier.weight(1f),
                                        textAlign = TextAlign.Center
                                    )
                                }

                                // Status column (right)
                                if (showOrganizerBooking) {
                                    Column(
                                        horizontalAlignment = Alignment.End,
                                        verticalArrangement = Arrangement.spacedBy(8.dp)
                                    ) {
                                        bookingForSlot?.let { booking ->
                                            val statusColor = EventCardAccent
                                            Surface(
                                                shape = RoundedCornerShape(16.dp),
                                                color = statusColor.copy(alpha = 0.12f),
                                                border = BorderStroke(1.dp, statusColor)
                                            ) {
                                                Text(
                                                    text = strings.bookingStatusText(booking.status),
                                                    modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
                                                    style = MaterialTheme.typography.labelMedium,
                                                    color = statusColor
                                                )
                                            }
                                            IconButton(
                                                onClick = {
                                                    val chatHint = formatClientDisplayName(booking)
                                                    onStartChatWithUser(booking.clientId.toString(), chatHint)
                                                }
                                            ) {
                                                Icon(Icons.Default.Send, contentDescription = strings.openChat)
                                            }
                                        }
                                    }
                                } else {
                                    val statusLabel = when (slotStatus) {
                                        TimeSlotStatus.AVAILABLE -> strings.slotAvailableLabel
                                        TimeSlotStatus.BOOKED -> strings.bookingStatusConfirmed
                                        TimeSlotStatus.BLOCKED -> strings.bookingStatusCancelled
                                        else -> strings.slotAvailableLabel
                                    }
                                    val statusColor = when (slotStatus) {
                                        TimeSlotStatus.AVAILABLE -> EventCardAccent
                                        TimeSlotStatus.BOOKED -> MaterialTheme.colorScheme.error
                                        TimeSlotStatus.BLOCKED -> MaterialTheme.colorScheme.error
                                        else -> EventCardAccent
                                    }
                                    Box(
                                        modifier = if (showUserTimezone) Modifier.weight(1f) else Modifier,
                                        contentAlignment = Alignment.CenterEnd
                                    ) {
                                        if (isSelectedSlot) {
                                            Icon(
                                                imageVector = Icons.Default.Check,
                                                contentDescription = null,
                                                tint = EventCardAccent
                                            )
                                        } else {
                                            Text(
                                                text = statusLabel,
                                                style = MaterialTheme.typography.labelMedium,
                                                color = statusColor
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        if (isOrganizer) {
            state.slotCancellationError?.let { error ->
                Text(
                    text = error,
                    color = MaterialTheme.colorScheme.error,
                    style = MaterialTheme.typography.bodyMedium
                )
            }

            Button(
                onClick = onRequestCancelSlots,
                enabled = state.selectedSlotIds.isNotEmpty() && !state.isCancellingSlots,
                modifier = Modifier
                    .fillMaxWidth()
                    .testTag(TestTags.EventCard.SLOT_CANCEL_SELECTED_SLOTS_BUTTON),
                colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error),
                shape = RoundedCornerShape(12.dp)
            ) {
                if (state.isCancellingSlots) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(18.dp),
                        strokeWidth = 2.dp,
                        color = MaterialTheme.colorScheme.onErrorContainer
                    )
                } else {
                    Icon(
                        imageVector = Icons.Default.EventBusy,
                        contentDescription = null,
                        modifier = Modifier.size(20.dp)
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(strings.cancelSelectedSlotsAction)
                }
            }

            state.restoreSlotsError?.let { error ->
                Text(
                    text = error,
                    color = MaterialTheme.colorScheme.error,
                    style = MaterialTheme.typography.bodyMedium
                )
            }

            OutlinedButton(
                onClick = onRequestRestoreSlots,
                enabled = state.selectedSlotIds.isNotEmpty() && !state.isRestoringSlots,
                modifier = Modifier
                    .fillMaxWidth()
                    .testTag(TestTags.EventCard.SLOT_RESTORE_SELECTED_SLOTS_BUTTON),
                shape = RoundedCornerShape(12.dp),
                border = BorderStroke(1.dp, EventCardAccent),
                colors = ButtonDefaults.outlinedButtonColors(
                    contentColor = EventCardAccentDark
                )
            ) {
                if (state.isRestoringSlots) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(18.dp),
                        strokeWidth = 2.dp
                    )
                } else {
                    Icon(
                        imageVector = Icons.Default.Restore,
                        contentDescription = null,
                        modifier = Modifier.size(20.dp)
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(strings.restoreSelectedSlotsAction)
                }
            }
        } else {
            if (shouldCollectContacts) {
                OutlinedTextField(
                    value = contactPhone,
                    onValueChange = {
                        contactPhone = it
                        contactPhoneError = if (it.isBlank()) null else if (isValidPhone(it)) null else strings.invalidPhone
                        contactFormError = null
                        contactSubmissionError = null
                        contactSubmitted = false
                    },
                    label = { Text(strings.phone) },
                    isError = contactPhoneError != null,
                    supportingText = {
                        contactPhoneError?.let { error ->
                            Text(
                                text = error,
                                color = MaterialTheme.colorScheme.error
                            )
                        }
                    },
                    singleLine = true,
                    shape = RoundedCornerShape(EventCardInputCorner),
                    colors = eventOutlinedFieldColors(),
                    modifier = Modifier
                        .fillMaxWidth()
                        .testTag(TestTags.EventCard.FREE_CONTACT_PHONE)
                )
                OutlinedTextField(
                    value = contactComment,
                    onValueChange = {
                        contactComment = it
                        contactFormError = null
                        contactSubmissionError = null
                        contactSubmitted = false
                    },
                    label = { Text(strings.comment) },
                    singleLine = false,
                    minLines = 2,
                    maxLines = 5,
                    shape = RoundedCornerShape(EventCardInputCorner),
                    colors = eventOutlinedFieldColors(),
                    modifier = Modifier
                        .fillMaxWidth()
                        .testTag(TestTags.EventCard.FREE_CONTACT_COMMENT)
                )
                contactFormError?.let { error ->
                    Text(
                        text = error,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall
                    )
                }
                contactSubmissionError?.let { error ->
                    Text(
                        text = error,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall
                    )
                }
                if (contactSubmitted) {
                    Text(
                        text = strings.contactSubmitted,
                        color = Color(0xFF2E7D32),
                        style = MaterialTheme.typography.bodySmall
                    )
                }
            }

            state.bookingError?.let { error ->
                Text(
                    text = error,
                    color = MaterialTheme.colorScheme.error,
                    style = MaterialTheme.typography.bodyMedium
                )
            }

            // Price breakdown with platform fee
            // Fallback to ticketPrice if priceTiers is empty
            val slotPrice = event.priceTiers?.firstOrNull()?.price ?: event.ticketPrice ?: 0.0
            val selectedCount = state.selectedSlotIds.size
            val slotTotal = slotPrice * selectedCount
            val serviceFee = platformFee.calculateFee(slotTotal)
            val grandTotal = slotTotal + serviceFee
            val isFreeSlotEvent = event.isFreeEvent()

            // Show price breakdown when slots are selected and event has a price
            if (selectedCount > 0 && slotPrice > 0) {
                Spacer(modifier = Modifier.height(8.dp))
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(
                            MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f),
                            RoundedCornerShape(12.dp)
                        )
                        .padding(12.dp),
                    verticalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    // Slots subtotal
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Text(
                            text = if (selectedCount > 1) "${strings.slotsLabel} ($selectedCount)" else strings.slotLabel,
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Text(
                            text = strings.formatPrice(slotTotal, event.currency),
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                    // Service fee (only if enabled and fee > 0)
                    if (platformFee.enabled && serviceFee > 0) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Text(
                                text = strings.serviceFeePercent((platformFee.feePercent * 100).toInt()),
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                            Text(
                                text = strings.formatPrice(serviceFee, event.currency),
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                    HorizontalDivider(
                        modifier = Modifier.padding(vertical = 4.dp),
                        color = MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)
                    )
                    // Total
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Text(
                            text = strings.totalAmount,
                            style = MaterialTheme.typography.bodyMedium,
                            fontWeight = FontWeight.Bold
                        )
                        Text(
                            text = strings.formatPrice(grandTotal, event.currency),
                            style = MaterialTheme.typography.bodyMedium,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
                Spacer(modifier = Modifier.height(8.dp))
            }

            val isProcessingBooking = state.isBooking || (shouldCollectContacts && isSubmittingContact)
            Button(
                onClick = {
                    if (isProcessingBooking || !bookingEnabled) return@Button
                    if (shouldCollectContacts) {
                        val validPhone = contactPhone.isNotBlank() && isValidPhone(contactPhone)
                        contactPhoneError = if (contactPhone.isBlank()) strings.phoneRequired else if (!validPhone) strings.invalidPhone else null

                        if (!validPhone) {
                            contactFormError = strings.checkContacts
                            return@Button
                        }

                        contactFormError = null
                        contactSubmissionError = null
                        isSubmittingContact = true
                        scope.launch {
                            contactApi.submitContact(
                                eventId = eventId,
                                phone = contactPhone.takeIf { it.isNotBlank() },
                                comment = contactComment.takeIf { it.isNotBlank() }
                            )
                                .onSuccess { contactRequest ->
                                    contactSubmitted = true
                                    contactRequestId = contactRequest.id
                                    onBookClick()
                                }
                                .onFailure { error ->
                                    contactSubmitted = false
                                    contactSubmissionError = error.message ?: "Contact submission failed"
                                }
                            isSubmittingContact = false
                        }
                    } else {
                        onBookClick()
                    }
                },
                enabled = state.selectedSlotIds.isNotEmpty() && !isProcessingBooking && bookingEnabled,
                modifier = Modifier
                    .fillMaxWidth()
                    .testTag(TestTags.EventCard.SLOT_BOOK_BUTTON),
                colors = ButtonDefaults.buttonColors(
                    containerColor = EventCardAccent,
                    contentColor = Color.White
                ),
                shape = RoundedCornerShape(14.dp)
            ) {
                if (isProcessingBooking) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(18.dp),
                        strokeWidth = 2.dp,
                        color = MaterialTheme.colorScheme.onPrimary
                    )
                } else {
                    Icon(
                        imageVector = Icons.Default.EventAvailable,
                        contentDescription = null,
                        modifier = Modifier.size(20.dp)
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    if (isFreeSlotEvent) {
                        Text(strings.book)
                    } else if (selectedCount > 0) {
                        Text("${strings.book} ${strings.formatPrice(grandTotal, event.currency)}")
                    } else {
                        Text(strings.book)
                    }
                }
            }
        }
    }
}

@Composable
private fun EventCancelledBanner(reason: String?) {
    val strings = strings()
    Surface(
        modifier = Modifier.fillMaxWidth(),
        color = MaterialTheme.colorScheme.errorContainer,
        shape = RoundedCornerShape(16.dp)
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Text(
                text = strings.eventCancelledTitle,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onErrorContainer
            )
            Text(
                text = strings.eventCancelledSubtitle,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onErrorContainer
            )
            reason?.takeIf { it.isNotBlank() }?.let {
                Text(
                    text = "${strings.cancellationReasonLabel}: $it",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onErrorContainer
                )
            }
        }
    }
}

@Composable
private fun OrganizerActionsCard(
    isEventCancelled: Boolean,
    isCancellingEvent: Boolean,
    cancelEventError: String?,
    isRestoringEvent: Boolean,
    restoreEventError: String?,
    isDeletingEvent: Boolean,
    deleteEventError: String?,
    onRequestCancelEvent: () -> Unit,
    onRequestRestoreEvent: () -> Unit,
    onEditEvent: () -> Unit,
    onRequestDeleteEvent: () -> Unit,
) {
    val strings = strings()
    EventSectionCard(title = strings.organizerActionsTitle) {
        EventManagementButtons(
            onEditClick = onEditEvent,
            onDeleteClick = onRequestDeleteEvent,
            isDeleting = isDeletingEvent,
            strings = strings,
            accentColor = EventCardAccentDark,
            outlineColor = EventCardOutline,
            dangerColor = MaterialTheme.colorScheme.error,
            shape = RoundedCornerShape(12.dp)
        )

        deleteEventError?.let {
            Text(
                text = it,
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodyMedium
            )
        }

        if (isEventCancelled) {
            Text(
                text = strings.restoreEventDescription,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            Button(
                onClick = onRequestRestoreEvent,
                enabled = !isRestoringEvent,
                modifier = Modifier.fillMaxWidth(),
                colors = ButtonDefaults.buttonColors(
                    containerColor = EventCardAccent,
                    contentColor = Color.White
                ),
                shape = RoundedCornerShape(14.dp)
            ) {
                if (isRestoringEvent) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(18.dp),
                        strokeWidth = 2.dp,
                        color = Color.White
                    )
                } else {
                    Icon(
                        imageVector = Icons.Default.Restore,
                        contentDescription = null,
                        modifier = Modifier.size(20.dp)
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(strings.restoreEventAction)
                }
            }
        } else {
            Text(
                text = strings.cancelEventDescription,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            Button(
                onClick = onRequestCancelEvent,
                enabled = !isCancellingEvent,
                modifier = Modifier
                    .fillMaxWidth()
                    .testTag(TestTags.EventCard.CANCEL_EVENT_BUTTON),
                colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error),
                shape = RoundedCornerShape(14.dp)
            ) {
                if (isCancellingEvent) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(18.dp),
                        strokeWidth = 2.dp,
                        color = MaterialTheme.colorScheme.onError
                    )
                } else {
                    Icon(
                        imageVector = Icons.Default.EventBusy,
                        contentDescription = null,
                        modifier = Modifier.size(20.dp)
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(strings.cancelEventAction)
                }
            }
        }
        cancelEventError?.let {
            Text(
                text = it,
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodyMedium
            )
        }
        restoreEventError?.let {
            Text(
                text = it,
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodyMedium
            )
        }
    }
}

private enum class CancellationDialogTarget {
    EVENT,
    RECURRING_DAY,
    SLOT_DAY,
    SLOTS,
}

@Composable
private fun CancellationReasonDialog(
    target: CancellationDialogTarget,
    selectedDate: LocalDate?,
    selectedSlotCount: Int,
    reason: String,
    error: String?,
    onReasonChange: (String) -> Unit,
    onDismiss: () -> Unit,
    onConfirm: () -> Unit,
) {
    val strings = strings()
    val title = when (target) {
        CancellationDialogTarget.EVENT -> strings.cancelEventAction
        CancellationDialogTarget.RECURRING_DAY -> strings.cancelSlotDayAction
        CancellationDialogTarget.SLOT_DAY -> strings.cancelSlotDayAction
        CancellationDialogTarget.SLOTS -> strings.cancelSelectedSlotsAction
    }
    val description = when (target) {
        CancellationDialogTarget.EVENT -> strings.cancelEventDescription
        CancellationDialogTarget.RECURRING_DAY -> strings.cancelSlotDayDescription
        CancellationDialogTarget.SLOT_DAY -> strings.cancelSlotDayDescription
        CancellationDialogTarget.SLOTS -> strings.cancelSelectedSlotsDescription
    }
    EventDialog(onDismissRequest = onDismiss) {
        Text(
            text = title,
            style = MaterialTheme.typography.titleLarge,
            fontWeight = FontWeight.SemiBold,
            color = EventCardAccentDark
        )
        Text(
            text = description,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        when (target) {
            CancellationDialogTarget.RECURRING_DAY,
            CancellationDialogTarget.SLOT_DAY -> {
                selectedDate?.let { date ->
                    Text(
                        text = "${strings.selectedDayLabel}: ${strings.formatSelectedDate(date)}",
                        style = MaterialTheme.typography.labelMedium,
                        fontWeight = FontWeight.SemiBold
                    )
                }
            }
            CancellationDialogTarget.SLOTS -> {
                Text(
                    text = "${strings.selectedSlotsLabel}: $selectedSlotCount",
                    style = MaterialTheme.typography.labelMedium,
                    fontWeight = FontWeight.SemiBold
                )
            }
            else -> Unit
        }
        OutlinedTextField(
            value = reason,
            onValueChange = onReasonChange,
            label = { Text(strings.cancellationReasonLabel) },
            placeholder = { Text(strings.cancellationReasonPlaceholder) },
            isError = error != null,
            minLines = 2,
            shape = RoundedCornerShape(EventCardInputCorner),
            colors = eventOutlinedFieldColors(),
            modifier = Modifier.testTag(TestTags.EventCard.CANCELLATION_REASON_FIELD)
        )
        error?.let {
            Text(
                text = it,
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodySmall
            )
        }
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.End,
            verticalAlignment = Alignment.CenterVertically
        ) {
            EventOutlineButton(
                text = strings.cancel,
                onClick = onDismiss
            )
            Spacer(modifier = Modifier.width(8.dp))
            EventPrimaryButton(
                text = strings.save,
                onClick = onConfirm,
                modifier = Modifier.testTag(TestTags.EventCard.CANCELLATION_CONFIRM_BUTTON)
            )
        }
    }
}

@Composable
private fun RecurringBookingSection(
    event: Event,
    state: RecurringBookingUiState,
    onPreviousMonth: () -> Unit,
    onNextMonth: () -> Unit,
    onDateSelected: (LocalDate) -> Unit,
    onBookClick: (Int) -> Unit,
    requireContactInfo: Boolean = false,
    ticketQuantity: Int,
    onQuantityChange: (Int) -> Unit,
    platformFee: PlatformFee = PlatformFee.DEFAULT,
    bookingEnabled: Boolean = true,
) {
    val strings = strings()
    val monthStart = state.calendarMonthStart ?: return
    val eventNow = currentDateTimeInTimeZone(event.timeZone)
    val today = eventNow.date
    val monthTitle = "${strings.monthNameFull(monthStart.monthNumberCompat())} ${monthStart.year}"
    val weekDaysShort = (0 until 7).map(strings::weekdayShortName)
    val weekDaysFull = (0 until 7).map(strings::weekdayName)
    val currentMonthStart = LocalDate(year = today.year, monthNumber = today.monthNumberCompat(), dayOfMonth = 1)
    val canGoPrevious = monthStart > currentMonthStart
    val scheduleDescription = when (state.scheduleType) {
        RecurringScheduleType.WEEKLY -> strings.recurringWeeklyDescription
        RecurringScheduleType.MONTHLY -> strings.recurringMonthlyDescription
        null -> null
    }
    val occurrenceTime = when (state.scheduleType) {
        RecurringScheduleType.MONTHLY -> event.monthlyTime
        RecurringScheduleType.WEEKLY -> event.weeklyTime
        null -> null
    } ?: event.eventTime
    fun isDatePast(date: LocalDate): Boolean {
        if (date < today) return true
        val time = occurrenceTime?.takeIf { it.isNotBlank() } ?: return false
        val occurrenceDateTime = combineDateAndTime(date, time)
        return occurrenceDateTime < eventNow
    }

    val availableDayNames = remember(state.availableDaysOfWeek) {
        state.availableDaysOfWeek.sorted().mapNotNull { index ->
            weekDaysFull.getOrNull(index)
        }
    }
    val shouldCollectContacts = requireContactInfo
    val allowQuantitySelection = state.scheduleType == RecurringScheduleType.WEEKLY
    val availableForSelectedDate = state.selectedDate?.let { date ->
        state.capacityByDate[date]
    }?.takeIf { it > 0 }
    val contactApi = remember { ContactApi() }
    val scope = rememberCoroutineScope()
    var contactPhone by remember { mutableStateOf("") }
    var contactComment by remember { mutableStateOf("") }
    var contactPhoneError by remember { mutableStateOf<String?>(null) }
    var contactFormError by remember { mutableStateOf<String?>(null) }
    var contactSubmissionError by remember { mutableStateOf<String?>(null) }
    var contactSubmitted by remember { mutableStateOf(false) }
    var isSubmittingContact by remember { mutableStateOf(false) }
    var contactRequestId by remember { mutableStateOf<Long?>(null) }
    var bookingIdLinked by remember { mutableStateOf(false) }

    // Update contact request with bookingId when booking is successful
    LaunchedEffect(state.pendingPaymentBookingId, contactRequestId) {
        if (state.pendingPaymentBookingId != null && contactRequestId != null && !bookingIdLinked) {
            contactApi.updateBookingId(event.id, contactRequestId!!, state.pendingPaymentBookingId)
                .onSuccess { bookingIdLinked = true }
        }
    }

    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        Text(
            text = strings.selectVisitDate,
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.Bold
        )

        scheduleDescription?.let { description ->
            Text(
                text = description,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }

        if (availableDayNames.isNotEmpty()) {
            Text(
                text = strings.availableDaysLabel(availableDayNames.joinToString(", ")),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }

        Card(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(20.dp),
            colors = CardDefaults.cardColors(containerColor = EventCardSurface),
            border = BorderStroke(1.dp, EventCardOutline),
            elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
        ) {
            Column(
                modifier = Modifier.padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    CalendarNavButton(
                        onClick = onPreviousMonth,
                        enabled = canGoPrevious,
                        icon = Icons.Default.ChevronLeft,
                        contentDescription = strings.previousMonth
                    )
                    Text(
                        text = monthTitle,
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        color = EventCardAccentDark
                    )
                    CalendarNavButton(
                        onClick = onNextMonth,
                        enabled = true,
                        icon = Icons.Default.ChevronRight,
                        contentDescription = strings.nextMonth
                    )
                }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    weekDaysShort.forEach { day ->
                        Text(
                            text = day,
                            modifier = Modifier.weight(1f),
                            textAlign = TextAlign.Center,
                            style = MaterialTheme.typography.labelMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                }

                val daysInMonth = monthStart.daysUntil(monthStart.plus(DatePeriod(months = 1)))
                val leadingEmpty = monthStart.dayOfWeek.isoDayNumber - 1
                val totalCells = leadingEmpty + daysInMonth
                val trailingEmpty = (7 - totalCells % 7) % 7
                val calendarDays = buildList<LocalDate?> {
                    repeat(leadingEmpty) { add(null) }
                    repeat(daysInMonth) { index ->
                        val date = monthStart.plus(DatePeriod(days = index))
                        add(if (isDatePast(date)) null else date)
                    }
                    repeat(trailingEmpty) { add(null) }
                }

                calendarDays.chunked(7).forEach { week ->
                    Row(modifier = Modifier.fillMaxWidth()) {
                        week.forEach { date ->
                            if (date == null) {
                                Spacer(
                                    modifier = Modifier
                                        .weight(1f)
                                        .height(48.dp)
                                )
                            } else {
                                val isSelected = date == state.selectedDate
                                val dayIsEnabled = state.availableDaysOfWeek.contains(date.dayOfWeek.isoDayNumber - 1)
                                val isBlocked = state.blockedDates.contains(date)
                                val isAvailable = dayIsEnabled && !isBlocked
                                val backgroundColor = when {
                                    isSelected -> EventCardAccent
                                    isAvailable -> EventCardAccentSoft
                                    else -> Color.Transparent
                                }
                                val textColor = when {
                                    isSelected -> Color.White
                                    isAvailable -> EventCardAccentDark
                                    else -> MaterialTheme.colorScheme.onSurfaceVariant
                                }

                                Box(
                                    modifier = Modifier
                                        .weight(1f)
                                        .height(48.dp)
                                        .padding(vertical = 4.dp)
                                        .clip(RoundedCornerShape(12.dp))
                                        .background(backgroundColor)
                                        .testTag("${TestTags.EventCard.RECURRING_DAY_PREFIX}${date}")
                                        .clickable(enabled = isAvailable && !state.isBooking) {
                                            onDateSelected(date)
                                        },
                                    contentAlignment = Alignment.Center
                                ) {
                                    Column(
                                        horizontalAlignment = Alignment.CenterHorizontally,
                                        verticalArrangement = Arrangement.Center
                                    ) {
                                        Text(
                                            text = date.dayOfMonth.toString(),
                                            style = MaterialTheme.typography.bodyMedium,
                                            color = textColor
                                        )
                                        val dailyCapacity = state.capacityByDate[date]
                                        if (dailyCapacity != null) {
                                            Text(
                                                text = dailyCapacity.toString(),
                                                style = MaterialTheme.typography.labelSmall,
                                                color = textColor
                                            )
                                        }
                                        if (isAvailable && !isSelected) {
                                            Box(
                                                modifier = Modifier
                                                    .padding(top = 4.dp)
                                                    .size(6.dp)
                                                    .clip(CircleShape)
                                                    .background(EventCardAccent)
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // Show remaining spots for selected date
        state.selectedDate?.let { date ->
            state.capacityByDate[date]?.let { remaining ->
                val total = event.maxCapacity
                val totalText = total?.let { " ${strings.of} $it" } ?: ""
                Text(
                    text = "${strings.spotsLeft}: $remaining$totalText",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.testTag("${TestTags.EventCard.RECURRING_SPOTS_PREFIX}${date}")
                )
            }
        }

        state.capacityError?.let { error ->
            Text(
                text = error,
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodySmall
            )
        }

        if (allowQuantitySelection) {
            TicketQuantitySelector(
                quantity = ticketQuantity,
                maxQuantity = availableForSelectedDate,
                available = availableForSelectedDate,
                onQuantityChange = onQuantityChange
            )
        }

        state.bookingError?.let { error ->
            Text(
                text = error,
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodyMedium
            )
        }

        val minTier = event.priceTiers?.minByOrNull { it.price }
        if (shouldCollectContacts) {
            OutlinedTextField(
                value = contactPhone,
                onValueChange = {
                    contactPhone = it
                    contactPhoneError = if (it.isBlank()) null else if (isValidPhone(it)) null else strings.invalidPhone
                    contactFormError = null
                    contactSubmissionError = null
                    contactSubmitted = false
                },
                label = { Text(strings.phone) },
                isError = contactPhoneError != null,
                supportingText = {
                    contactPhoneError?.let { error ->
                        Text(
                            text = error,
                            color = MaterialTheme.colorScheme.error
                        )
                    }
                },
                singleLine = true,
                shape = RoundedCornerShape(EventCardInputCorner),
                colors = eventOutlinedFieldColors(),
                modifier = Modifier
                    .fillMaxWidth()
                    .testTag(TestTags.EventCard.FREE_CONTACT_PHONE)
            )
            OutlinedTextField(
                value = contactComment,
                onValueChange = {
                    contactComment = it
                    contactFormError = null
                    contactSubmissionError = null
                    contactSubmitted = false
                },
                label = { Text(strings.comment) },
                singleLine = false,
                minLines = 2,
                maxLines = 5,
                shape = RoundedCornerShape(EventCardInputCorner),
                colors = eventOutlinedFieldColors(),
                modifier = Modifier
                    .fillMaxWidth()
                    .testTag(TestTags.EventCard.FREE_CONTACT_COMMENT)
            )
            contactFormError?.let { error ->
                Text(
                    text = error,
                    color = MaterialTheme.colorScheme.error,
                    style = MaterialTheme.typography.bodySmall
                )
            }
            contactSubmissionError?.let { error ->
                Text(
                    text = error,
                    color = MaterialTheme.colorScheme.error,
                    style = MaterialTheme.typography.bodySmall
                )
            }
            if (contactSubmitted) {
                Text(
                    text = strings.contactSubmitted,
                    color = Color(0xFF2E7D32),
                    style = MaterialTheme.typography.bodySmall
                )
            }
        }

        val bookingQuantity = if (allowQuantitySelection) ticketQuantity else 1

        // Price breakdown with platform fee
        // Fallback to ticketPrice if priceTiers is empty
        val tierPrice = minTier?.price ?: event.ticketPrice ?: 0.0
        val ticketTotal = tierPrice * bookingQuantity
        val serviceFee = platformFee.calculateFee(ticketTotal)
        val grandTotal = ticketTotal + serviceFee

        // Show price breakdown when date is selected and event has a price
        if (state.selectedDate != null && ticketTotal > 0) {
            Spacer(modifier = Modifier.height(8.dp))
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(
                        MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f),
                        RoundedCornerShape(12.dp)
                    )
                    .padding(12.dp),
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                // Ticket subtotal
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text(
                        text = if (bookingQuantity > 1) "${strings.ticketQuantityLabel} ($bookingQuantity)" else strings.ticketQuantityLabel,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Text(
                        text = strings.formatPrice(ticketTotal, event.currency),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                // Service fee (only if enabled and fee > 0)
                if (platformFee.enabled && serviceFee > 0) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Text(
                            text = strings.serviceFeePercent((platformFee.feePercent * 100).toInt()),
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Text(
                            text = strings.formatPrice(serviceFee, event.currency),
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                }
                HorizontalDivider(
                    modifier = Modifier.padding(vertical = 4.dp),
                    color = MaterialTheme.colorScheme.outline.copy(alpha = 0.5f)
                )
                // Total
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text(
                        text = strings.totalAmount,
                        style = MaterialTheme.typography.bodyMedium,
                        fontWeight = FontWeight.Bold
                    )
                    Text(
                        text = strings.formatPrice(grandTotal, event.currency),
                        style = MaterialTheme.typography.bodyMedium,
                        fontWeight = FontWeight.Bold
                    )
                }
            }
            Spacer(modifier = Modifier.height(8.dp))
        }

        val isProcessingBooking = state.isBooking || (shouldCollectContacts && isSubmittingContact)
        Button(
            onClick = {
                if (isProcessingBooking || !bookingEnabled) return@Button
                if (shouldCollectContacts) {
                    val validPhone = contactPhone.isNotBlank() && isValidPhone(contactPhone)
                    contactPhoneError = if (contactPhone.isBlank()) strings.phoneRequired else if (!validPhone) strings.invalidPhone else null

                    if (!validPhone) {
                        contactFormError = strings.checkContacts
                        return@Button
                    }

                    contactFormError = null
                    contactSubmissionError = null
                    isSubmittingContact = true
                    scope.launch {
                        contactApi.submitContact(
                            eventId = event.id,
                            phone = contactPhone.takeIf { it.isNotBlank() },
                            comment = contactComment.takeIf { it.isNotBlank() }
                        )
                            .onSuccess { contactRequest ->
                                contactSubmitted = true
                                contactRequestId = contactRequest.id
                                onBookClick(bookingQuantity)
                            }
                            .onFailure { error ->
                                contactSubmitted = false
                                contactSubmissionError = error.message ?: "Contact submission failed"
                            }
                        isSubmittingContact = false
                    }
                } else {
                    onBookClick(bookingQuantity)
                }
            },
            enabled = state.selectedDate != null && !isProcessingBooking && bookingEnabled,
            modifier = Modifier
                .fillMaxWidth()
                .testTag(TestTags.EventCard.RECURRING_BOOK_BUTTON),
            colors = ButtonDefaults.buttonColors(
                containerColor = EventCardAccent,
                contentColor = Color.White
            ),
            shape = RoundedCornerShape(14.dp)
        ) {
            if (isProcessingBooking) {
                CircularProgressIndicator(
                    modifier = Modifier.size(18.dp),
                    strokeWidth = 2.dp,
                    color = MaterialTheme.colorScheme.onPrimary
                )
            } else {
                Icon(
                    imageVector = Icons.Default.EventAvailable,
                    contentDescription = null,
                    modifier = Modifier.size(20.dp)
                )
                Spacer(modifier = Modifier.width(8.dp))
                val displayTotal = if (platformFee.enabled && serviceFee > 0) grandTotal else ticketTotal
                val priceText = if (displayTotal > 0) {
                    strings.formatPrice(displayTotal, event.currency)
                } else null
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(6.dp)
                ) {
                    Text(
                        text = priceText?.let { strings.bookForPrice(it) } ?: strings.book,
                        style = MaterialTheme.typography.titleMedium
                    )
                    if (allowQuantitySelection && bookingQuantity > 1) {
                        Text(
                            text = "x$bookingQuantity",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.SemiBold
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun TicketQuantitySelector(
    quantity: Int,
    maxQuantity: Int? = null,
    available: Int? = null,
    minQuantity: Int = 1,
    onQuantityChange: (Int) -> Unit,
    modifier: Modifier = Modifier,
    decreaseButtonTag: String? = null,
    increaseButtonTag: String? = null,
    valueTag: String? = null,
    availableTag: String? = null
) {
    val strings = strings()
    val canDecrease = quantity > minQuantity
    val canIncrease = maxQuantity?.let { quantity < it } ?: true

    EventSectionCard(
        title = strings.ticketQuantityLabel,
        modifier = modifier
    ) {
        Surface(
            color = EventCardSurface,
            shape = RoundedCornerShape(EventCardInputCorner),
            tonalElevation = 1.dp,
            border = BorderStroke(1.dp, EventCardOutline)
        ) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(56.dp)
                    .padding(horizontal = 8.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                EventStepperButton(
                    onClick = { if (canDecrease) onQuantityChange(quantity - 1) },
                    enabled = canDecrease,
                    icon = Icons.Default.Remove,
                    contentDescription = strings.decreaseQuantity,
                    modifier = decreaseButtonTag?.let { Modifier.testTag(it) } ?: Modifier
                )
                Text(
                    text = quantity.toString(),
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.Bold,
                    color = EventCardAccentDark,
                    modifier = valueTag?.let { Modifier.testTag(it) } ?: Modifier
                )
                EventStepperButton(
                    onClick = { if (canIncrease) onQuantityChange(quantity + 1) },
                    enabled = canIncrease,
                    icon = Icons.Default.Add,
                    contentDescription = strings.increaseQuantity,
                    modifier = increaseButtonTag?.let { Modifier.testTag(it) } ?: Modifier
                )
            }
        }
        available?.let { remaining ->
            Text(
                text = "${strings.spotsLeft}: $remaining",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = availableTag?.let { Modifier.testTag(it) } ?: Modifier
            )
        }
    }
}

@Composable
private fun PriceBadge(price: String, modifier: Modifier = Modifier) {
    Surface(
        modifier = modifier,
        shape = RoundedCornerShape(999.dp),
        color = EventCardAccentSoft,
        border = BorderStroke(1.dp, EventCardAccent.copy(alpha = 0.35f))
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 14.dp, vertical = 8.dp),
            horizontalArrangement = Arrangement.spacedBy(6.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = price,
                style = MaterialTheme.typography.labelLarge,
                fontWeight = FontWeight.SemiBold,
                color = EventCardAccentDark
            )
        }
    }
}

@Composable
private fun EventTag(
    icon: ImageVector,
    text: String,
    backgroundColor: Color,
    textColor: Color = Color.White
) {
    Surface(
        color = backgroundColor,
        shape = RoundedCornerShape(999.dp),
        border = BorderStroke(1.dp, textColor.copy(alpha = 0.2f))
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
            horizontalArrangement = Arrangement.spacedBy(4.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                imageVector = icon,
                contentDescription = null,
                tint = textColor,
                modifier = Modifier.size(16.dp)
            )
            Text(
                text = text,
                style = MaterialTheme.typography.labelMedium,
                color = textColor,
                fontWeight = FontWeight.Medium
            )
        }
    }
}

@Composable
private fun InfoCard(
    icon: ImageVector,
    title: String,
    content: String,
    timeInfo: String?,
    modifier: Modifier = Modifier,
    timeInfoTag: String? = null,
    durationInfo: String? = null,
    localTimeInfo: AnnotatedString? = null
) {
    Card(
        modifier = modifier,
        colors = CardDefaults.cardColors(
            containerColor = EventCardSurface
        ),
        shape = RoundedCornerShape(20.dp),
        border = BorderStroke(1.dp, EventCardOutline),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    tint = EventCardAccent,
                    modifier = Modifier.size(20.dp)
                )
                Text(
                    text = title,
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }

            Text(
                text = content,
                style = MaterialTheme.typography.bodyLarge,
                fontWeight = FontWeight.Bold
            )

            timeInfo?.let { info ->
                Text(
                    text = info,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = timeInfoTag?.let { Modifier.testTag(it) } ?: Modifier
                )
            }

            localTimeInfo?.let { localInfo ->
                Text(
                    text = localInfo,
                    style = MaterialTheme.typography.bodySmall,
                    color = EventCardAccentDark
                )
            }

            durationInfo?.takeIf { it.isNotBlank() }?.let { duration ->
                Text(
                    text = duration,
                    style = MaterialTheme.typography.bodySmall,
                    color = EventCardAccentDark
                )
            }
        }
    }
}

@Composable
private fun InfoCardCompact(
    icon: ImageVector,
    title: String,
    timeInfo: String,
    timezoneLabel: String,
    durationInfo: String?,
    modifier: Modifier = Modifier
) {
    Card(
        modifier = modifier,
        colors = CardDefaults.cardColors(
            containerColor = EventCardSurface
        ),
        shape = RoundedCornerShape(20.dp),
        border = BorderStroke(1.dp, EventCardOutline),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(
            modifier = Modifier.padding(12.dp),
            verticalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            // Timezone label at the top - larger font
            Text(
                text = timezoneLabel,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = EventCardAccent
            )

            // Date
            Text(
                text = title,
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.SemiBold
            )

            // Time range
            Text(
                text = timeInfo,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )

            // Duration - larger font
            durationInfo?.takeIf { it.isNotBlank() }?.let { duration ->
                Text(
                    text = duration,
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.Medium,
                    color = EventCardAccentDark
                )
            }
        }
    }
}

@Composable
private fun InfoRow(
    icon: ImageVector,
    title: String,
    content: String
) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(12.dp),
        verticalAlignment = Alignment.Top
    ) {
        Surface(
            color = EventCardAccentSoft,
            shape = CircleShape,
            modifier = Modifier.size(40.dp)
        ) {
            Box(contentAlignment = Alignment.Center) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    tint = EventCardAccent,
                    modifier = Modifier.size(20.dp)
                )
            }
        }

        Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Text(
                text = title,
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            Text(
                text = content,
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Medium
            )
        }
    }
}

@Composable
private fun ReviewCard(
    reviewerName: String,
    rating: Double,
    timeAgo: String,
    avatarUrl: String?
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = EventCardSurface
        ),
        border = BorderStroke(1.dp, EventCardOutline),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp)
    ) {
        Row(
            modifier = Modifier.padding(16.dp),
            horizontalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            // Avatar
            Surface(
                modifier = Modifier.size(48.dp),
                shape = CircleShape,
                color = EventCardAccentSoft
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Text(
                        text = reviewerName.first().uppercase(),
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold,
                        color = EventCardAccent
                    )
                }
            }

            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = reviewerName,
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = FontWeight.Bold
                    )

                    Row(
                        horizontalArrangement = Arrangement.spacedBy(4.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(
                            imageVector = Icons.Default.Star,
                            contentDescription = null,
                            tint = Color(0xFFFFB800),
                            modifier = Modifier.size(16.dp)
                        )
                        Text(
                            text = rating.toString(),
                            style = MaterialTheme.typography.labelLarge,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }

                Text(
                    text = timeAgo,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    }
}

/**
 * Displays form data from landing page in a readable format.
 * Converts JSON keys to human-readable labels.
 */
@Composable
private fun FormDataDisplay(
    formData: kotlinx.serialization.json.JsonObject,
    modifier: Modifier = Modifier
) {
    val colorScheme = MaterialTheme.colorScheme

    Column(modifier = modifier) {
        formData.entries.forEach { (key, value) ->
            val displayKey = formatFormFieldKey(key)
            val displayValue = value.toString().trim('"') // Remove JSON quotes

            if (displayValue.isNotBlank()) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(vertical = 2.dp),
                    horizontalArrangement = Arrangement.Start
                ) {
                    Text(
                        text = "$displayKey: ",
                        style = MaterialTheme.typography.bodyMedium,
                        fontWeight = FontWeight.Medium,
                        color = colorScheme.onSurfaceVariant
                    )
                    Text(
                        text = displayValue,
                        style = MaterialTheme.typography.bodyMedium,
                        color = colorScheme.onSurface
                    )
                }
            }
        }
    }
}

/**
 * Converts form field keys to human-readable labels.
 * Examples: "user_name" -> "User name", "firstName" -> "First Name", "phone" -> "Phone"
 */
private fun formatFormFieldKey(key: String): String {
    return key
        .replace("_", " ")
        .replace(Regex("([a-z])([A-Z])")) { "${it.groupValues[1]} ${it.groupValues[2]}" }
        .lowercase()
        .replaceFirstChar { it.uppercase() }
}

@Composable
private fun PhoneLinkText(
    text: String,
    phone: String,
    style: TextStyle,
    color: Color = MaterialTheme.colorScheme.onSurface,
    modifier: Modifier = Modifier,
) {
    val uriHandler = LocalUriHandler.current
    val clipboardManager = LocalClipboardManager.current
    val isWeb = remember { getPlatform().isWeb }
    val dialUri = remember(phone) { formatTelUri(phone) }
    val copyText = remember(phone) { phone.trim() }
    SelectionContainer {
        Text(
            text = text,
            style = style,
            color = color,
            modifier = when {
                isWeb && copyText.isNotEmpty() -> modifier.clickable {
                    clipboardManager.setText(AnnotatedString(copyText))
                }
                dialUri != null -> modifier.clickable { uriHandler.openUri(dialUri) }
                else -> modifier
            }
        )
    }
}

@Composable
private fun EmailLinkText(
    text: String,
    email: String,
    style: TextStyle,
    color: Color = MaterialTheme.colorScheme.onSurface,
    modifier: Modifier = Modifier,
) {
    val uriHandler = LocalUriHandler.current
    val clipboardManager = LocalClipboardManager.current
    val isWeb = remember { getPlatform().isWeb }
    val copyText = remember(email) { email.trim() }
    val mailtoUri = remember(copyText) { formatMailtoUri(copyText) }
    SelectionContainer {
        Text(
            text = text,
            style = style,
            color = color,
            modifier = when {
                isWeb && copyText.isNotEmpty() -> modifier.clickable {
                    clipboardManager.setText(AnnotatedString(copyText))
                }
                mailtoUri != null -> modifier.clickable { uriHandler.openUri(mailtoUri) }
                else -> modifier
            }
        )
    }
}

// Helper functions
private fun formatSlotRange(slot: TimeSlot, timeZone: TimeZone = TimeZone.currentSystemDefault()): String {
    return formatSlotRange(slot.startTime, slot.endTime, timeZone)
}

private fun formatSlotRange(start: Instant, end: Instant, timeZone: TimeZone = TimeZone.currentSystemDefault()): String {
    return formatSlotRange(
        start = start.toLocalDateTime(timeZone),
        end = end.toLocalDateTime(timeZone)
    )
}

private fun formatSlotRange(start: LocalDateTime, end: LocalDateTime): String {
    return "${start.hour.twoDigits()}:${start.minute.twoDigits()} - ${end.hour.twoDigits()}:${end.minute.twoDigits()}"
}

/**
 * Format client display name prioritizing firstName + lastName over username
 */
private fun formatClientDisplayName(booking: Booking): String {
    val firstName = booking.clientFirstName?.trim()?.takeIf { it.isNotBlank() }
    val lastName = booking.clientLastName?.trim()?.takeIf { it.isNotBlank() }

    return when {
        firstName != null && lastName != null -> "$firstName $lastName"
        firstName != null -> firstName
        lastName != null -> lastName
        else -> booking.clientName?.takeIf { it.isNotBlank() }
            ?: booking.clientEmail?.takeIf { it.isNotBlank() }
            ?: booking.clientId.toString()
    }
}

private fun Strings.bookingStatusText(status: BookingStatus): String = when (status) {
    BookingStatus.RESERVED,
    BookingStatus.PENDING -> bookingStatusPending
    BookingStatus.CONFIRMED -> bookingStatusConfirmed
    BookingStatus.CANCELLED -> bookingStatusCancelled
    BookingStatus.COMPLETED -> bookingStatusCompleted
    BookingStatus.NO_SHOW -> bookingStatusNoShow
    BookingStatus.EXPIRED -> bookingStatusCancelled
}

private fun formatTelUri(phone: String): String? {
    val trimmed = phone.trim()
    if (trimmed.isEmpty()) return null
    val sanitized = buildString {
        trimmed.forEach { char ->
            if (char.isDigit() || char == '+') {
                append(char)
            }
        }
    }
    return if (sanitized.isEmpty()) null else "tel:$sanitized"
}

private fun formatMailtoUri(email: String): String? {
    val trimmed = email.trim()
    if (trimmed.isEmpty()) return null
    return if (trimmed.contains("@")) "mailto:$trimmed" else null
}

private fun Int.twoDigits(): String = toString().padStart(2, '0')

private fun Strings.formatEventDate(start: Instant?, end: Instant?, timeZone: String? = null): String {
    val startValue = formatDateReadable(start, timeZone) ?: return dateTbd
    val zone = if (!timeZone.isNullOrBlank()) createTimeZone(timeZone) else TimeZone.UTC
    val startDate = start?.toLocalDateTime(zone)?.date
    val endDate = end?.toLocalDateTime(zone)?.date
    val endValue = when {
        endDate == null -> null
        startDate != null && endDate == startDate -> null
        else -> formatDateReadable(end, timeZone)
    }
    return if (endValue == null) startValue else "$startValue - $endValue"
}

private fun Strings.formatSelectedDate(date: LocalDate): String {
    return "${date.dayOfMonth} ${monthNameGenitive(date.monthNumberCompat())}"
}

private fun Strings.formatDateReadable(instant: Instant?, timeZone: String? = null): String? {
    if (instant == null) return null
    return try {
        val localDateTime = if (!timeZone.isNullOrBlank()) {
            instant.toLocalDateTimeInTimeZone(timeZone)
        } else {
            instant.toLocalDateTime(TimeZone.UTC)
        }
        val year = localDateTime.year
        val month = monthNameGenitive(localDateTime.monthNumber)
        val day = localDateTime.dayOfMonth
        "$day $month, $year"
    } catch (e: Exception) {
        instant.toString().take(10)
    }
}

/**
 * Formats recurring event time as a range (e.g., "12:00 - 14:00")
 * Takes start time string (HH:mm) and calculates end time from duration
 */
private fun formatRecurringTimeRange(startTime: String, durationMinutes: Int?): String {
    if (durationMinutes == null || durationMinutes <= 0) return startTime

    return try {
        val timeParts = startTime.split(":")
        if (timeParts.size < 2) return startTime

        val startHour = timeParts[0].toIntOrNull() ?: return startTime
        val startMinute = timeParts[1].toIntOrNull() ?: return startTime

        val endMinutes = startHour * 60 + startMinute + durationMinutes
        val endHour = (endMinutes / 60) % 24
        val endMinute = endMinutes % 60
        val endTime = "${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}"

        "$startTime - $endTime"
    } catch (e: Exception) {
        startTime
    }
}

private fun formatEventTime(instant: Instant?, durationMinutes: Int?, timeZone: String? = null, includeTimezone: Boolean = true): String {
    if (instant == null) return ""

    return try {
        val localDateTime = if (!timeZone.isNullOrBlank()) {
            instant.toLocalDateTimeInTimeZone(timeZone)
        } else {
            instant.toLocalDateTime(TimeZone.UTC)
        }
        val startHour = localDateTime.hour
        val startMinute = localDateTime.minute
        val startTime = "${startHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}"

        val timeRange = if (durationMinutes != null && durationMinutes > 0) {
            val endMinutes = startHour * 60 + startMinute + durationMinutes
            val endHour = (endMinutes / 60) % 24
            val endMinute = endMinutes % 60
            val endTime = "${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}"
            "$startTime - $endTime"
        } else {
            startTime
        }

        if (includeTimezone && !timeZone.isNullOrBlank()) {
            val tzAbbreviation = getTimezoneAbbreviation(timeZone)
            "$timeRange ($tzAbbreviation)"
        } else {
            timeRange
        }
    } catch (e: Exception) {
        ""
    }
}

private fun getTimezoneAbbreviation(timeZoneId: String): String {
    // Extract meaningful abbreviation from timezone ID
    // e.g., "America/Vancouver" -> "Vancouver", "Europe/Moscow" -> "Moscow"
    return timeZoneId.substringAfterLast("/").replace("_", " ")
}

private fun findNextAvailableDate(availableDaysOfWeek: Set<Int>): LocalDate? {
    if (availableDaysOfWeek.isEmpty()) return null
    val today = currentLocalDate()
    // Search up to 7 days to find the next available day
    for (i in 0..6) {
        val date = today.plus(DatePeriod(days = i))
        val dayOfWeekIndex = date.dayOfWeek.isoDayNumber - 1 // Convert to 0-based index (Mon=0, Sun=6)
        if (availableDaysOfWeek.contains(dayOfWeekIndex)) {
            return date
        }
    }
    return null
}

private fun formatRecurringTimeInUserTimezone(
    date: LocalDate,
    time: String,
    durationMinutes: Int?,
    eventTimeZone: String?,
    userTimeZoneId: String
): AnnotatedString? {
    if (eventTimeZone.isNullOrBlank()) return null
    if (eventTimeZone == userTimeZoneId) return null

    return try {
        // Parse time string (expected format "HH:mm")
        val timeParts = time.split(":")
        if (timeParts.size < 2) return null
        val hour = timeParts[0].toIntOrNull() ?: return null
        val minute = timeParts[1].toIntOrNull() ?: return null

        // Combine date and time to get an Instant in event's timezone
        val eventDateTime = LocalDateTime(date.year, date.monthNumber, date.dayOfMonth, hour, minute)
        val instant = eventDateTime.toInstantInTimeZone(eventTimeZone)

        // Convert to user's timezone
        val userLocalDateTime = instant.toLocalDateTimeInTimeZone(userTimeZoneId)

        // Check if times are identical (same timezone offset)
        if (eventDateTime == userLocalDateTime) return null

        val startHour = userLocalDateTime.hour
        val startMinute = userLocalDateTime.minute
        val startTime = "${startHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}"

        val timeRange = if (durationMinutes != null && durationMinutes > 0) {
            val endMinutes = startHour * 60 + startMinute + durationMinutes
            val endHour = (endMinutes / 60) % 24
            val endMinute = endMinutes % 60
            val endTime = "${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}"
            "$startTime - $endTime"
        } else {
            startTime
        }

        val userDate = userLocalDateTime.date
        val dateStr = "${userDate.dayOfMonth}.${userDate.monthNumber.toString().padStart(2, '0')}"
        val userTzAbbreviation = getTimezoneAbbreviation(userTimeZoneId)

        buildAnnotatedString {
            withStyle(SpanStyle(fontWeight = FontWeight.Bold)) {
                append(dateStr)
            }
            append(" $timeRange ($userTzAbbreviation)")
        }
    } catch (e: Exception) {
        null
    }
}

private fun formatEventTimeInUserTimezone(
    instant: Instant?,
    durationMinutes: Int?,
    eventTimeZone: String?,
    userTimeZoneId: String
): AnnotatedString? {
    if (instant == null) return null
    if (eventTimeZone.isNullOrBlank()) return null

    return try {
        // Check if timezones are the same
        if (eventTimeZone == userTimeZoneId) return null

        // Convert to both timezones and check if times are actually the same
        val eventLocalDateTime = instant.toLocalDateTimeInTimeZone(eventTimeZone)
        val userLocalDateTime = instant.toLocalDateTimeInTimeZone(userTimeZoneId)

        // If the times are identical, no need to show duplicate
        if (eventLocalDateTime == userLocalDateTime) return null

        val startHour = userLocalDateTime.hour
        val startMinute = userLocalDateTime.minute
        val startTime = "${startHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}"

        val timeRange = if (durationMinutes != null && durationMinutes > 0) {
            val endMinutes = startHour * 60 + startMinute + durationMinutes
            val endHour = (endMinutes / 60) % 24
            val endMinute = endMinutes % 60
            val endTime = "${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}"
            "$startTime - $endTime"
        } else {
            startTime
        }

        val date = userLocalDateTime.date
        val dateStr = "${date.dayOfMonth}.${date.monthNumber.toString().padStart(2, '0')}"
        val userTzAbbreviation = getTimezoneAbbreviation(userTimeZoneId)

        buildAnnotatedString {
            withStyle(SpanStyle(fontWeight = FontWeight.Bold)) {
                append(dateStr)
            }
            append(" $timeRange ($userTzAbbreviation)")
        }
    } catch (e: Exception) {
        null
    }
}

/**
 * Returns only the time range string in user timezone (e.g., "10:00 - 12:00")
 */
private fun formatRecurringTimeRangeInUserTimezone(
    date: LocalDate,
    time: String,
    durationMinutes: Int?,
    eventTimeZone: String,
    userTimeZoneId: String
): String? {
    return try {
        val timeParts = time.split(":")
        if (timeParts.size < 2) return null
        val hour = timeParts[0].toIntOrNull() ?: return null
        val minute = timeParts[1].toIntOrNull() ?: return null

        val eventDateTime = LocalDateTime(date.year, date.monthNumber, date.dayOfMonth, hour, minute)
        val instant = eventDateTime.toInstantInTimeZone(eventTimeZone)
        val userLocalDateTime = instant.toLocalDateTimeInTimeZone(userTimeZoneId)

        val startHour = userLocalDateTime.hour
        val startMinute = userLocalDateTime.minute
        val startTime = "${startHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}"

        if (durationMinutes != null && durationMinutes > 0) {
            val endMinutes = startHour * 60 + startMinute + durationMinutes
            val endHour = (endMinutes / 60) % 24
            val endMinute = endMinutes % 60
            val endTime = "${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}"
            "$startTime - $endTime"
        } else {
            startTime
        }
    } catch (e: Exception) {
        null
    }
}

/**
 * Returns the date in user timezone as LocalDate
 */
private fun getRecurringDateInUserTimezone(
    date: LocalDate,
    time: String,
    eventTimeZone: String,
    userTimeZoneId: String
): LocalDate? {
    return try {
        val timeParts = time.split(":")
        if (timeParts.size < 2) return null
        val hour = timeParts[0].toIntOrNull() ?: return null
        val minute = timeParts[1].toIntOrNull() ?: return null

        val eventDateTime = LocalDateTime(date.year, date.monthNumber, date.dayOfMonth, hour, minute)
        val instant = eventDateTime.toInstantInTimeZone(eventTimeZone)
        val userLocalDateTime = instant.toLocalDateTimeInTimeZone(userTimeZoneId)

        userLocalDateTime.date
    } catch (e: Exception) {
        null
    }
}

/**
 * Returns only the time range string in user timezone for one-time events
 */
private fun formatEventTimeRangeInUserTimezone(
    instant: Instant,
    durationMinutes: Int?,
    eventTimeZone: String,
    userTimeZoneId: String
): String? {
    return try {
        val userLocalDateTime = instant.toLocalDateTimeInTimeZone(userTimeZoneId)

        val startHour = userLocalDateTime.hour
        val startMinute = userLocalDateTime.minute
        val startTime = "${startHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}"

        if (durationMinutes != null && durationMinutes > 0) {
            val endMinutes = startHour * 60 + startMinute + durationMinutes
            val endHour = (endMinutes / 60) % 24
            val endMinute = endMinutes % 60
            val endTime = "${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}"
            "$startTime - $endTime"
        } else {
            startTime
        }
    } catch (e: Exception) {
        null
    }
}

/**
 * Returns the date in user timezone as LocalDate for one-time events
 */
private fun getEventDateInUserTimezone(
    instant: Instant,
    userTimeZoneId: String
): LocalDate? {
    return try {
        val userLocalDateTime = instant.toLocalDateTimeInTimeZone(userTimeZoneId)
        userLocalDateTime.date
    } catch (e: Exception) {
        null
    }
}

private fun Strings.formatPrice(price: Double, currency: String?): String {
    val symbol = when (currency?.uppercase()) {
        "RUB", "RUR" -> "₽"
        "USD" -> "$"
        "EUR" -> "€"
        else -> currency.orEmpty()
    }
    if (price == 0.0) return free
    val roundedCents = (price * 100).roundToInt()
    val whole = roundedCents / 100
    val fraction = abs(roundedCents % 100)
    val value = if (fraction == 0) {
        whole.toString()
    } else {
        "${whole}.${fraction.toString().padStart(2, '0')}"
    }
    return buildString {
        append(value)
        if (symbol.isNotBlank()) {
            append(' ')
            append(symbol)
        }
    }
}

/**
 * Determines if a URL is a video based on extension or if it's in the videoUrls set
 */
private fun isVideoUrl(url: String, videoUrls: Set<String>): Boolean {
    // Check by file extension first (most reliable)
    val lowerUrl = url.lowercase()
    val isVideoByExtension = lowerUrl.endsWith(".mp4") ||
           lowerUrl.endsWith(".mov") ||
           lowerUrl.endsWith(".webm") ||
           lowerUrl.endsWith(".avi") ||
           lowerUrl.endsWith(".mkv") ||
           lowerUrl.startsWith("data:video", ignoreCase = true)

    if (isVideoByExtension) {
        return true
    }

    // Check if URL is in the videoUrls set
    // videoUrls contain relative paths like "events/uuid/videos/video-1.mp4"
    // mediaUrls contain full paths like "/api/media/events/uuid/videos/video-1.mp4"
    // So we need to check if the URL ends with or contains the videoUrl path
    return videoUrls.any { videoUrl ->
        url == videoUrl ||
        url.endsWith(videoUrl) ||
        url.contains(videoUrl) ||
        videoUrl.endsWith(url.substringAfterLast("/")) ||
        url.substringAfterLast("/") == videoUrl.substringAfterLast("/")
    }
}

private fun isImageUrl(url: String): Boolean {
    val lowerUrl = url.lowercase()
    return lowerUrl.startsWith("data:image", ignoreCase = true) ||
            lowerUrl.endsWith(".jpg") ||
            lowerUrl.endsWith(".jpeg") ||
            lowerUrl.endsWith(".png") ||
            lowerUrl.endsWith(".gif") ||
            lowerUrl.endsWith(".webp")
}

private fun resolveEventMediaUrl(
    originalUrl: String,
    isVideo: Boolean,
    thumbWidth: Int?
): String {
    val baseUrl = ApiClient.getBaseUrl()
    val isAbsolute = originalUrl.startsWith("http://") || originalUrl.startsWith("https://")
    val isData = originalUrl.startsWith("data:")
    if (isAbsolute && !originalUrl.startsWith(baseUrl)) {
        return originalUrl
    }
    if (isAbsolute || isData) {
        val apiRelative = if (isAbsolute && originalUrl.startsWith(baseUrl)) {
            originalUrl.removePrefix(baseUrl)
        } else {
            originalUrl
        }
        val thumbPath = if (!isVideo && thumbWidth != null && thumbWidth > 0 && apiRelative.startsWith("/api/media/")) {
            toThumbUrl(apiRelative, thumbWidth)
        } else {
            apiRelative
        }
        return resolveMediaUrl(thumbPath) ?: originalUrl
    }

    val normalized = if (originalUrl.startsWith("/")) originalUrl else "/$originalUrl"
    val apiRelative = if (normalized.startsWith("/api/media/")) normalized else "/api/media$normalized"
    val thumbPath = if (!isVideo && thumbWidth != null && thumbWidth > 0) {
        toThumbUrl(apiRelative, thumbWidth)
    } else {
        apiRelative
    }
    return resolveMediaUrl(thumbPath) ?: originalUrl
}

private data class MediaItem(
    val url: String,
    val isVideo: Boolean
)
